/* =========================================================================
   DOMAIN LOCK
   ========================================================================= */
(function () {
  var host = window.location.hostname;
  var allowed = ['dupercut.com', 'dupercut.myshopify.com'];
  if (!allowed.some(function (h) {
    return host === h || host.endsWith('.' + h) || host === 'localhost' || host === '127.0.0.1';
  })) {
    document.body.innerHTML = '';
    throw new Error('Unauthorized domain');
  }
}());

/* =========================================================================
   CONFIG
   ========================================================================= */

const MIN_SIDE_KM = 3, MAX_SIDE_KM = 500, DEFAULT_SIDE_KM = 10;
const INCHES_TO_MM = 25.4;
const DEFAULT_W_IN = 10, DEFAULT_H_IN = 10;
const MIN_DIM_IN = 4, MAX_DIM_IN = 40, STEP_IN = 0.25;
const MIN_DIM_MM = 100, MAX_DIM_MM = 1020, STEP_MM = 5;

const SHALLOW_SMOOTH_MM = 10;
const BAND_1_MM = 2.5;
const BAND_2_MM = 6;
const BAND_3_MM = 12;
const BAND_4_MM = 20;
const BAND_5_MM = 30;

const MAJOR_ROAD_CLASSES = ['motorway', 'trunk', 'primary', 'secondary'];
const MINOR_ROAD_CLASSES = ['tertiary', 'minor', 'service'];
// Road classes eligible for street name labels. Includes tertiary (named
// streets in towns/suburbs) in addition to the major classes.
const LABEL_ROAD_CLASSES = ['motorway', 'trunk', 'primary', 'secondary', 'tertiary', 'minor'];

// Detail-boost: temporarily fetches tiles at a fixed high zoom (behind the
// loading overlay) so road/label detail no longer depends on how zoomed-out
// the user happened to be when they framed a large area. See boostMapDetail().
const DETAIL_ZOOM_TARGET = 15;      // most OpenMapTiles-based styles cap real detail around z14; 15 gives headroom for deeper sources without cost (MapLibre just serves the deepest available tile if the source doesn't go this deep)
const DETAIL_CANVAS_MAX_PX = 3200;  // cap on the temporary canvas size, so huge areas degrade gracefully instead of requesting an enormous tile set
const DETAIL_WAIT_MS = 12000;       // cap on how long we wait for the boosted tile set to load
const DETAIL_SKIP_ROAD_COUNT = 400; // if this many road segments are already loaded at the current zoom, the area is already road-dense (a city, not open countryside) -- boosting further would mean fetching and processing a huge amount of extra data for a result that's barely distinguishable, so the boost is skipped entirely
const ROAD_SMOOTH_MAX_SEGMENTS = 1500; // above this many raw segments for one road class, skip chaining + spline smoothing and use the raw lines -- in a dense city the streets are short and packed close enough that the curve is barely visible anyway, so it's not worth the cost

const CLIPPER_SCALE = 100;

// Google Fonts TTF URLs for opentype.js text-to-path conversion
const FONT_URLS = {
  "'Great Vibes',cursive": 'https://raw.githubusercontent.com/google/fonts/main/ofl/greatvibes/GreatVibes-Regular.ttf',
  "'Dancing Script',cursive": 'https://raw.githubusercontent.com/google/fonts/main/ofl/dancingscript/DancingScript%5Bwght%5D.ttf',
  "Parisienne,cursive": 'https://raw.githubusercontent.com/google/fonts/main/ofl/parisienne/Parisienne-Regular.ttf',
  "Sacramento,cursive": 'https://raw.githubusercontent.com/google/fonts/main/ofl/sacramento/Sacramento-Regular.ttf',
  "'Pinyon Script',cursive": 'https://raw.githubusercontent.com/google/fonts/main/ofl/pinyonscript/PinyonScript-Regular.ttf',
  "'Cormorant Garamond',serif": 'https://raw.githubusercontent.com/google/fonts/main/ofl/cormorantgaramond/CormorantGaramond%5Bwght%5D.ttf',
  "Cinzel,serif": 'https://raw.githubusercontent.com/google/fonts/main/ofl/cinzel/Cinzel%5Bwght%5D.ttf',
  "'Alex Brush',cursive": 'https://raw.githubusercontent.com/google/fonts/main/ofl/alexbrush/AlexBrush-Regular.ttf',
  "Inter,Helvetica,sans-serif": 'https://raw.githubusercontent.com/google/fonts/main/ofl/inter/Inter%5Bopsz%2Cwght%5D.ttf',
};
const loadedFonts = {};

async function loadFont(fontFamily) {
  if (loadedFonts[fontFamily]) return loadedFonts[fontFamily];
  const url = FONT_URLS[fontFamily];
  if (!url || typeof opentype === 'undefined') return null;
  try {
    console.log('Loading font:', url);
    const font = await opentype.load(url);
    loadedFonts[fontFamily] = font;
    return font;
  } catch (e) {
    console.warn('Font load failed:', fontFamily, e);
    return null;
  }
}

// Convert text to ClipperLib polygon paths using opentype.js
// Measure text width using a hidden SVG element (matches actual SVG rendering)
const _measureSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
_measureSvg.setAttribute('style', 'position:absolute;visibility:hidden;width:0;height:0;overflow:hidden;');
const _measureTxt = document.createElementNS('http://www.w3.org/2000/svg', 'text');
_measureSvg.appendChild(_measureTxt);
document.addEventListener('DOMContentLoaded', () => document.body.appendChild(_measureSvg));
if (document.body) document.body.appendChild(_measureSvg);

function measureTextWidth(text, fontFamily, fontSize, fontWeight) {
  _measureTxt.setAttribute('font-family', fontFamily);
  _measureTxt.setAttribute('font-size', fontSize);
  _measureTxt.setAttribute('font-weight', fontWeight || '800');
  _measureTxt.textContent = text;
  return _measureTxt.getComputedTextLength();
}

// Convert opentype.js path commands into polygon rings (flattening beziers
// to line segments). Shared by textToPolygons (straight, horizontal text)
// and textToPolygonsAlongPath (curved, per-letter text) below so both stay
// in sync -- this is exactly the ring-extraction logic textToPolygons always
// used, just factored out so it can be reused per-glyph.
function commandsToRings(commands) {
  const rings = [];
  let current = [];
  for (const cmd of commands) {
    if (cmd.type === 'M') {
      if (current.length >= 3) rings.push(current);
      current = [{ x: cmd.x, y: cmd.y }];
    } else if (cmd.type === 'L') {
      current.push({ x: cmd.x, y: cmd.y });
    } else if (cmd.type === 'C') {
      // Approximate cubic bezier with line segments
      const prev = current[current.length - 1] || { x: 0, y: 0 };
      for (let t = 0.2; t <= 1; t += 0.2) {
        const u = 1 - t;
        current.push({
          x: u*u*u*prev.x + 3*u*u*t*cmd.x1 + 3*u*t*t*cmd.x2 + t*t*t*cmd.x,
          y: u*u*u*prev.y + 3*u*u*t*cmd.y1 + 3*u*t*t*cmd.y2 + t*t*t*cmd.y,
        });
      }
    } else if (cmd.type === 'Q') {
      const prev = current[current.length - 1] || { x: 0, y: 0 };
      for (let t = 0.25; t <= 1; t += 0.25) {
        const u = 1 - t;
        current.push({
          x: u*u*prev.x + 2*u*t*cmd.x1 + t*t*cmd.x,
          y: u*u*prev.y + 2*u*t*cmd.y1 + t*t*cmd.y,
        });
      }
    } else if (cmd.type === 'Z') {
      if (current.length >= 3) rings.push(current);
      current = [];
    }
  }
  if (current.length >= 3) rings.push(current);
  return rings;
}

function textToPolygons(text, font, fontSize, x, y) {
  if (!font || !text) return [];
  const path = font.getPath(text, x, y, fontSize);
  // Collect all glyph rings, then UNION them to eliminate overlap gaps
  const rings = commandsToRings(path.commands);
  // Union all rings to merge overlapping letters (prevents gaps in script fonts)
  if (rings.length > 1) {
    try {
      const merged = unionPolygons(rings);
      return merged.length ? merged : rings;
    } catch(e) { return rings; }
  }
  return rings;
}

// Places `text` along `points` (an already-tessellated polyline, in mm) with
// EACH LETTER given its own position and rotation, sampled from the path's
// local tangent at that letter's own arc-length spot -- not one rotation
// applied to the whole word. `points` should be the same chained + smoothed
// road polyline that gets drawn on screen, so the label always follows the
// exact curve that's visible. Returns unioned polygon rings, or null if the
// text doesn't fit within the path's length (caller should skip in that case).
function textToPolygonsAlongPath(text, font, fontSize, points, offsetDist, anchorS) {
  if (!font || !text || !points || points.length < 2) return null;
  offsetDist = offsetDist || 0;

  const cum = [0];
  for (let i = 1; i < points.length; i++) {
    cum.push(cum[i - 1] + Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y));
  }
  const total = cum[cum.length - 1];

  const chars = Array.from(text);
  const tracking = fontSize * 0.02;
  const advances = chars.map(c => font.getAdvanceWidth(c, fontSize) + tracking);
  const textWidth = advances.reduce((a, b) => a + b, 0);

  const labelStart = anchorS != null ? anchorS - textWidth / 2 : (total - textWidth) / 2;
  const labelEnd = labelStart + textWidth;
  function rawPosAt(s) {
    s = Math.max(0, Math.min(total, s));
    let i = 0;
    while (i < cum.length - 2 && cum[i + 1] < s) i++;
    const segLen = (cum[i + 1] - cum[i]) || 1e-6;
    const t = (s - cum[i]) / segLen;
    const p0 = points[i], p1 = points[i + 1];
    return { x: p0.x + (p1.x - p0.x) * t, y: p0.y + (p1.y - p0.y) * t };
  }
  const pS = rawPosAt(Math.max(0, labelStart)), pE = rawPosAt(Math.min(total, labelEnd));
  const avgAngle = Math.atan2(pE.y - pS.y, pE.x - pS.x);
  const offX = -Math.sin(avgAngle) * offsetDist;
  const offY =  Math.cos(avgAngle) * offsetDist;

  function sampleAt(s) {
    if (s < 0) {
      const p0 = points[0], p1 = points[Math.min(1, points.length - 1)];
      const angle = Math.atan2(p1.y - p0.y, p1.x - p0.x);
      return { x: p0.x + Math.cos(angle) * s + offX, y: p0.y + Math.sin(angle) * s + offY, angle };
    }
    if (s > total) {
      const p0 = points[points.length - 2] || points[0], p1 = points[points.length - 1];
      const angle = Math.atan2(p1.y - p0.y, p1.x - p0.x);
      const excess = s - total;
      return { x: p1.x + Math.cos(angle) * excess + offX, y: p1.y + Math.sin(angle) * excess + offY, angle };
    }
    let i = 0;
    while (i < cum.length - 2 && cum[i + 1] < s) i++;
    const segLen = (cum[i + 1] - cum[i]) || 1e-6;
    const t = (s - cum[i]) / segLen;
    const p0 = points[i], p1 = points[i + 1];
    const halfSz = fontSize * 0.8;
    const tA = rawPosAt(Math.max(0, s - halfSz));
    const tB = rawPosAt(Math.min(total, s + halfSz));
    return {
      x: p0.x + (p1.x - p0.x) * t + offX,
      y: p0.y + (p1.y - p0.y) * t + offY,
      angle: Math.atan2(tB.y - tA.y, tB.x - tA.x),
    };
  }

  let s = labelStart;
  const allRings = [];
  for (let ci = 0; ci < chars.length; ci++) {
    const adv = advances[ci];
    if (chars[ci] !== ' ') {
      const mid = sampleAt(s + adv / 2);
      const glyphPath = font.getPath(chars[ci], -adv / 2, 0, fontSize);
      const rings = commandsToRings(glyphPath.commands);
      const cos = Math.cos(mid.angle), sin = Math.sin(mid.angle);
      for (const ring of rings) {
        allRings.push(ring.map(p => ({
          x: mid.x + p.x * cos - p.y * sin,
          y: mid.y + p.x * sin + p.y * cos,
        })));
      }
    }
    s += adv;
  }
  if (!allRings.length) return null;
  try {
    const merged = unionPolygons(allRings);
    return merged.length ? merged : allRings;
  } catch (e) { return allRings; }
}
const EARTH_RADIUS_M = 6371000;


/* =========================================================================
   UTILITIES
   ========================================================================= */

async function fetchWithTimeout(url, options = {}, timeoutMs = 12000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
}

/* =========================================================================
   GEOCODING — Photon (primary) + Nominatim (fallback)
   ========================================================================= */

// Calculates the approximate km span of a bounding box
function extentToKm(south, north, west, east) {
  const latMid = (south + north) / 2;
  const kmLat = (north - south) * 110.574;
  const kmLon = (east - west) * 111.320 * Math.cos(latMid * Math.PI / 180);
  return { width: Math.abs(kmLon), height: Math.abs(kmLat) };
}

function buildDisplayName(props) {
  const parts = [props.name, props.city, props.state, props.country].filter(Boolean);
  return [...new Set(parts)].join(', ') || 'Unknown location';
}

async function tryPhoton(placeName, timeoutMs) {
  const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(placeName)}&limit=1`;
  const res = await fetchWithTimeout(url, { headers: { 'Accept': 'application/json' } }, timeoutMs);
  if (!res.ok) throw new Error(`Photon HTTP ${res.status}`);
  const data = await res.json();
  if (!data.features || !data.features.length) return null;
  const feat = data.features[0];
  const [lon, lat] = feat.geometry.coordinates;
  // Photon returns extent: [west, south, east, north] for area features
  const ext = feat.properties.extent;
  const extentKm = ext ? extentToKm(ext[1], ext[3], ext[0], ext[2]) : null;
  return { lat, lon, displayName: buildDisplayName(feat.properties), extentKm };
}

async function tryNominatim(placeName, timeoutMs) {
  const params = new URLSearchParams({ format: 'json', limit: '1', q: placeName, email: 'maps@duperlab.com' });
  const res = await fetchWithTimeout(`https://nominatim.openstreetmap.org/search?${params}`, { headers: { 'Accept': 'application/json' } }, timeoutMs);
  if (!res.ok) throw new Error(`Nominatim HTTP ${res.status}`);
  const results = await res.json();
  if (!results.length) return null;
  const r = results[0];
  // Nominatim returns boundingbox: [south, north, west, east]
  const bb = r.boundingbox;
  const extentKm = bb ? extentToKm(parseFloat(bb[0]), parseFloat(bb[1]), parseFloat(bb[2]), parseFloat(bb[3])) : null;
  return { lat: parseFloat(r.lat), lon: parseFloat(r.lon), displayName: r.display_name, extentKm };
}

async function geocodePlace(placeName, onStatus) {
  const providers = [
    { name: 'Photon', fn: tryPhoton },
    { name: 'Nominatim', fn: tryNominatim },
  ];
  let lastError = null;
  for (let i = 0; i < providers.length; i++) {
    const { name, fn } = providers[i];
    if (i > 0 && onStatus) onStatus(`Trying ${name}…`);
    try {
      const result = await fn(placeName, 10000);
      if (result) return result;
      throw new Error(`Couldn't find "${placeName}". Try a more specific name.`);
    } catch (err) {
      if (err.message && err.message.startsWith("Couldn't find")) throw err;
      lastError = err;
      continue;
    }
  }
  const detail = lastError ? ` (${lastError.name || 'Error'}: ${lastError.message || 'unknown'})` : '';
  if (location.protocol === 'file:') {
    throw new Error(`Browser is blocking network requests from a local file.${detail} Try Firefox, or run: python3 -m http.server`);
  }
  throw new Error(`Could not reach any place-lookup server.${detail}`);
}

/* =========================================================================
   VECTOR TILE LOADING (MapLibre GL JS + OpenFreeMap)
   Pre-built, CDN-cached tiles — loads in ~1-2 seconds vs 10-30s for
   Overpass, and never times out because there's no server-side query.
   ========================================================================= */


function makeProjector(centerLat, centerLon) {
  const phi0 = (centerLat * Math.PI) / 180;
  const lambda0 = (centerLon * Math.PI) / 180;
  const n = Math.sin(phi0);
  const F = Math.cos(phi0) * Math.pow(Math.tan(Math.PI / 4 + phi0 / 2), n) / n;
  const rho0 = EARTH_RADIUS_M * F / Math.pow(Math.tan(Math.PI / 4 + phi0 / 2), n);

  return function project(lat, lon) {
    const phi = (lat * Math.PI) / 180;
    const lambda = (lon * Math.PI) / 180;
    const rho = EARTH_RADIUS_M * F / Math.pow(Math.tan(Math.PI / 4 + phi / 2), n);
    const theta = n * (lambda - lambda0);
    const x = rho * Math.sin(theta);
    const y = rho0 - rho * Math.cos(theta);
    return { x, y: -y };
  };
}

/* =========================================================================
   GEOMETRY — ClipperLib helpers (mm, scaled to integers)
   ========================================================================= */

function mmToClipper(points) {
  return points.map((p) => ({ X: Math.round(p.x * CLIPPER_SCALE), Y: Math.round(p.y * CLIPPER_SCALE) }));
}
function clipperToMm(paths) {
  return paths.map((path) => path.map((p) => ({ x: p.X / CLIPPER_SCALE, y: p.Y / CLIPPER_SCALE })));
}

function unionPolygons(polygonsMm) {
  if (!polygonsMm.length) return [];
  const clipper = new ClipperLib.Clipper();
  clipper.AddPaths(polygonsMm.map(mmToClipper), ClipperLib.PolyType.ptSubject, true);
  const solution = new ClipperLib.Paths();
  clipper.Execute(ClipperLib.ClipType.ctUnion, solution, ClipperLib.PolyFillType.pftNonZero, ClipperLib.PolyFillType.pftNonZero);
  return clipperToMm(solution);
}

function differencePolygons(subjectMm, clipMm) {
  if (!subjectMm.length) return [];
  const clipper = new ClipperLib.Clipper();
  clipper.AddPaths(subjectMm.map(mmToClipper), ClipperLib.PolyType.ptSubject, true);
  if (clipMm.length) clipper.AddPaths(clipMm.map(mmToClipper), ClipperLib.PolyType.ptClip, true);
  const solution = new ClipperLib.Paths();
  clipper.Execute(ClipperLib.ClipType.ctDifference, solution, ClipperLib.PolyFillType.pftNonZero, ClipperLib.PolyFillType.pftNonZero);
  return clipperToMm(solution);
}

function intersectPolygons(subjectMm, clipMm) {
  if (!subjectMm.length || !clipMm.length) return [];
  const clipper = new ClipperLib.Clipper();
  clipper.AddPaths(subjectMm.map(mmToClipper), ClipperLib.PolyType.ptSubject, true);
  clipper.AddPaths(clipMm.map(mmToClipper), ClipperLib.PolyType.ptClip, true);
  const solution = new ClipperLib.Paths();
  clipper.Execute(ClipperLib.ClipType.ctIntersection, solution, ClipperLib.PolyFillType.pftNonZero, ClipperLib.PolyFillType.pftNonZero);
  return clipperToMm(solution);
}

function cleanupPolygons(polygonsMm) {
  if (!polygonsMm.length) return [];
  const scaled = polygonsMm.map(mmToClipper);
  const simplified = ClipperLib.Clipper.SimplifyPolygons(scaled, ClipperLib.PolyFillType.pftNonZero);
  const cleaned = ClipperLib.Clipper.CleanPolygons(simplified, 0.15 * CLIPPER_SCALE);
  return clipperToMm(cleaned);
}

function offsetPolygons(polygonsMm, deltaMm) {
  if (!polygonsMm.length) return [];
  const offset = new ClipperLib.ClipperOffset();
  offset.AddPaths(polygonsMm.map(mmToClipper), ClipperLib.JoinType.jtRound, ClipperLib.EndType.etClosedPolygon);
  const solution = new ClipperLib.Paths();
  offset.Execute(solution, deltaMm * CLIPPER_SCALE);
  return clipperToMm(solution);
}

/* -------------------------------------------------------------------------
   ORGANIC SHALLOW/DEEP BOUNDARY
   Smooth sine-wave wobble applied to the clean offset, then rendered with
   Catmull-Rom curves (see ringToSmoothPathD) for a flowy contour.
   ------------------------------------------------------------------------- */

// --- 2D SPATIAL NOISE ---
// Each vertex is displaced based on its (x,y) position, NOT its distance
// along the perimeter. This makes each band's contour an independent
// artistic shape rather than a uniform offset of the coastline.

function hashNoise(ix, iy, seed) {
  let h = (ix * 374761393 + iy * 668265263 + seed * 1274126177) | 0;
  h = Math.imul(h ^ (h >>> 13), 1103515245);
  return ((h ^ (h >>> 16)) & 0x7fffffff) / 0x7fffffff * 2 - 1;
}

function noise2D(x, y, seed, scale) {
  const sx = x / scale, sy = y / scale;
  const ix = Math.floor(sx), iy = Math.floor(sy);
  const fx = sx - ix, fy = sy - iy;
  const u = fx * fx * (3 - 2 * fx);
  const v = fy * fy * (3 - 2 * fy);
  const a = hashNoise(ix, iy, seed), b = hashNoise(ix + 1, iy, seed);
  const c = hashNoise(ix, iy + 1, seed), d = hashNoise(ix + 1, iy + 1, seed);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}

function fractalNoise2D(x, y, seed, baseScale, octaves) {
  let val = 0, amp = 1, total = 0;
  for (let i = 0; i < octaves; i++) {
    val += noise2D(x, y, seed + i * 997, baseScale / (1 << i)) * amp;
    total += amp;
    amp *= 0.35; // lower persistence = smoother, more organic shapes
  }
  return val / total;
}

function pathLength(points) {
  let len = 0;
  for (let i = 1; i < points.length; i++) len += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
  return len;
}

function polygonCentroid(points) {
  let x = 0, y = 0;
  for (const p of points) { x += p.x; y += p.y; }
  return { x: x / points.length, y: y / points.length };
}

// Displaces each vertex along its local normal by a value from the 2D noise
// field. `noiseScale` controls feature size (larger = broader undulations).
// `amplitude` controls how far the contour deviates from the uniform offset.
function displaceRingWithNoise(ring, amplitude, seed, noiseScale) {
  const centroid = polygonCentroid(ring);
  return ring.map((curr, i) => {
    const prev = ring[(i - 1 + ring.length) % ring.length];
    const next = ring[(i + 1) % ring.length];
    const tx = next.x - prev.x, ty = next.y - prev.y;
    const tLen = Math.hypot(tx, ty) || 1;
    let nx = -ty / tLen, ny = tx / tLen;
    const towardCentroidDot = nx * (centroid.x - curr.x) + ny * (centroid.y - curr.y);
    if (towardCentroidDot > 0) { nx = -nx; ny = -ny; }
    const n = fractalNoise2D(curr.x, curr.y, seed, noiseScale, 4) * amplitude;
    return { x: curr.x + nx * n, y: curr.y + ny * n };
  });
}

function displacePolygonsWithNoise(polygons, amplitude, seed, noiseScale) {
  return polygons.map((ring) => displaceRingWithNoise(ring, amplitude, seed, noiseScale));
}

// Chaikin's corner-cutting: produces genuinely smooth, organic curves
// from angular ClipperLib polygons. Each iteration doubles the vertex
// count and rounds all corners into flowing curves.
function chaikinSmooth(ring, iterations) {
  let r = ring;
  for (let iter = 0; iter < iterations; iter++) {
    const n = [];
    for (let i = 0; i < r.length; i++) {
      const a = r[i], b = r[(i + 1) % r.length];
      n.push({ x: a.x * 0.75 + b.x * 0.25, y: a.y * 0.75 + b.y * 0.25 });
      n.push({ x: a.x * 0.25 + b.x * 0.75, y: a.y * 0.25 + b.y * 0.75 });
    }
    r = n;
  }
  return r;
}

function chaikinSmoothPolygons(polygons, iterations) {
  return polygons.map(ring => chaikinSmooth(ring, iterations));
}

// Chaikin smoothing for an OPEN path (a road, not a closed ring). The first
// and last points are kept exactly fixed so smoothed roads still connect
// properly at intersections/tile boundaries; only interior joints get
// corner-cut into curves.
function chaikinSmoothOpenPath(points, iterations) {
  if (points.length < 3) return points;
  let pts = points;
  for (let iter = 0; iter < iterations; iter++) {
    const next = [pts[0]];
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i], b = pts[i + 1];
      next.push({ x: a.x * 0.75 + b.x * 0.25, y: a.y * 0.75 + b.y * 0.25 });
      next.push({ x: a.x * 0.25 + b.x * 0.75, y: a.y * 0.25 + b.y * 0.75 });
    }
    next.push(pts[pts.length - 1]);
    pts = next;
  }
  return pts;
}

// Removes tiny polygon fragments (corner artifacts, slivers from boolean ops)
function filterTinyPolygons(polygons, minAreaMmSq) {
  return polygons.filter(ring => {
    let area = 0;
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i], b = ring[(i + 1) % ring.length];
      area += a.x * b.y - b.x * a.y;
    }
    return Math.abs(area / 2) >= minAreaMmSq;
  });
}

// Adds intermediate vertices along long edges so Catmull-Rom has evenly
// spaced control points. Without this, tile-edge geometry with few vertices
// produces long flat sections followed by tight bends — the opposite of
// the smooth, flowing contour we want.
function resampleRing(ring, maxGap) {
  if (ring.length < 3) return ring;
  const out = [];
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    out.push(a);
    const dx = b.x - a.x, dy = b.y - a.y;
    const dist = Math.hypot(dx, dy);
    if (dist > maxGap) {
      const steps = Math.ceil(dist / maxGap);
      for (let s = 1; s < steps; s++) {
        const t = s / steps;
        out.push({ x: a.x + dx * t, y: a.y + dy * t });
      }
    }
  }
  return out;
}

function bufferLinesToPolygons(linesMm, widthMm) {
  if (!linesMm.length) return [];
  const offset = new ClipperLib.ClipperOffset();
  const paths = linesMm.map(mmToClipper).filter((p) => p.length >= 2);
  offset.AddPaths(paths, ClipperLib.JoinType.jtRound, ClipperLib.EndType.etOpenRound);
  const solution = new ClipperLib.Paths();
  offset.Execute(solution, (widthMm / 2) * CLIPPER_SCALE);
  return unionPolygons(clipperToMm(solution));
}

// Liang-Barsky clip of a single segment against [-halfW,halfW] × [-halfH,halfH].
function clipSegmentToRect(p0, p1, halfW, halfH) {
  let t0 = 0, t1 = 1;
  const dx = p1.x - p0.x, dy = p1.y - p0.y;
  const checks = [[-dx, p0.x + halfW], [dx, halfW - p0.x], [-dy, p0.y + halfH], [dy, halfH - p0.y]];
  for (const [p, q] of checks) {
    if (p === 0) { if (q < 0) return null; continue; }
    const r = q / p;
    if (p < 0) { if (r > t1) return null; if (r > t0) t0 = r; }
    else { if (r < t0) return null; if (r < t1) t1 = r; }
  }
  if (t0 > t1) return null;
  return { p0: { x: p0.x + t0 * dx, y: p0.y + t0 * dy }, p1: { x: p0.x + t1 * dx, y: p0.y + t1 * dy }, t1 };
}

function clipPolylineToRect(points, halfW, halfH) {
  const subpaths = [];
  let current = [];
  for (let i = 0; i < points.length - 1; i++) {
    const clipped = clipSegmentToRect(points[i], points[i + 1], halfW, halfH);
    if (!clipped) { if (current.length) { subpaths.push(current); current = []; } continue; }
    const last = current[current.length - 1];
    if (!last || Math.abs(last.x - clipped.p0.x) > 1e-6 || Math.abs(last.y - clipped.p0.y) > 1e-6) {
      if (current.length) subpaths.push(current);
      current = [clipped.p0];
    }
    current.push(clipped.p1);
    if (clipped.t1 < 1) { subpaths.push(current); current = []; }
  }
  if (current.length) subpaths.push(current);
  return subpaths;
}



/* =========================================================================
   PIPELINE
   ========================================================================= */

function tessellateSmooth(ring, segsPerEdge) {
  const n = ring.length;
  if (n < 3) return ring;
  const at = (i) => ring[((i % n) + n) % n];
  const out = [];
  for (let i = 0; i < n; i++) {
    const p0 = at(i - 1), p1 = at(i), p2 = at(i + 1), p3 = at(i + 2);
    const c1x = p1.x + (p2.x - p0.x) / 6, c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6, c2y = p2.y - (p3.y - p1.y) / 6;
    for (let s = 0; s < segsPerEdge; s++) {
      const t = s / segsPerEdge, u = 1 - t;
      out.push({
        x: u*u*u*p1.x + 3*u*u*t*c1x + 3*u*t*t*c2x + t*t*t*p2.x,
        y: u*u*u*p1.y + 3*u*u*t*c1y + 3*u*t*t*c2y + t*t*t*p2.y,
      });
    }
  }
  return out;
}
function tessellatePolygons(polygons, segs) { return polygons.map(r => tessellateSmooth(r, segs || 4)); }

// Open-path Catmull-Rom smoothing for roads (not closed rings). Unlike
// Chaikin corner-cutting, this uses the points BEFORE and AFTER each segment
// to compute real curve tangents -- so it can render a genuine curve across
// a stretch of road even where the immediate two endpoints of one piece look
// straight, as long as the surrounding points show the road bending. End
// points are clamped (no wraparound) so the path's true start/end stay
// fixed, which matters once pieces are chained together at intersections.
function tessellateSmoothOpenPath(points, segsPerEdge) {
  const n = points.length;
  if (n < 3) return points;
  const at = (i) => points[Math.max(0, Math.min(n - 1, i))];
  const out = [];
  for (let i = 0; i < n - 1; i++) {
    const p0 = at(i - 1), p1 = at(i), p2 = at(i + 1), p3 = at(i + 2);
    const c1x = p1.x + (p2.x - p0.x) / 6, c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6, c2y = p2.y - (p3.y - p1.y) / 6;
    const steps = (i === n - 2) ? segsPerEdge + 1 : segsPerEdge;
    for (let s = 0; s < steps; s++) {
      const t = s / segsPerEdge, u = 1 - t;
      out.push({
        x: u*u*u*p1.x + 3*u*u*t*c1x + 3*u*t*t*c2x + t*t*t*p2.x,
        y: u*u*u*p1.y + 3*u*u*t*c1y + 3*u*t*t*c2y + t*t*t*p2.y,
      });
    }
  }
  return out;
}

// OSM roads are frequently split into separate way features at every
// intersection, even when the real street continues straight through or
// curves gently across several of them. Smoothing each piece in isolation
// (as the previous version of this code did) can't see that -- each piece's
// endpoints get preserved exactly, so the road still looks faceted overall
// even though each individual piece got smoothed. This groups same-named
// pieces and chains any that share an endpoint (within a small coordinate
// tolerance) into one longer polyline before smoothing runs, so curvature
// spanning several intersections is visible to the smoothing pass. Unnamed
// pieces are left ungrouped (each is its own chain of one).
function chainNamedSegments(segments) {
  const TOL = 0.5; // mm
  const key = (p) => Math.round(p.x / TOL) + ',' + Math.round(p.y / TOL);

  const groups = new Map();
  // Tracks the real street name (not the name-or-ref grouping key) per
  // group, purely so callers that want it can label the chained result --
  // doesn't affect which pieces get grouped/chained together.
  const groupLabelNames = new Map();
  let unnamedCounter = 0;
  for (const seg of segments) {
    const gkey = seg.name ? ('n:' + seg.name) : ('u:' + (unnamedCounter++));
    if (!groups.has(gkey)) groups.set(gkey, []);
    groups.get(gkey).push(seg.pts);
    if (seg.labelName && !groupLabelNames.has(gkey)) groupLabelNames.set(gkey, seg.labelName);
  }

  const result = [];
  for (const [gkey, pieces] of groups) {
    const labelName = groupLabelNames.get(gkey) || null;
    if (pieces.length === 1) { result.push({ pts: pieces[0], labelName }); continue; }

    const endpointMap = new Map();
    const addEndpoint = (k, idx, end) => {
      if (!endpointMap.has(k)) endpointMap.set(k, []);
      endpointMap.get(k).push({ idx, end });
    };
    pieces.forEach((pts, idx) => {
      addEndpoint(key(pts[0]), idx, 'start');
      addEndpoint(key(pts[pts.length - 1]), idx, 'end');
    });

    const used = new Array(pieces.length).fill(false);
    for (let i = 0; i < pieces.length; i++) {
      if (used[i]) continue;
      used[i] = true;
      let chain = pieces[i].slice();

      let extending = true;
      while (extending) {
        extending = false;
        const candidates = endpointMap.get(key(chain[chain.length - 1])) || [];
        for (const c of candidates) {
          if (used[c.idx]) continue;
          const piece = pieces[c.idx];
          chain = c.end === 'start' ? chain.concat(piece.slice(1)) : chain.concat(piece.slice(0, -1).reverse());
          used[c.idx] = true; extending = true; break;
        }
      }
      extending = true;
      while (extending) {
        extending = false;
        const candidates = endpointMap.get(key(chain[0])) || [];
        for (const c of candidates) {
          if (used[c.idx]) continue;
          const piece = pieces[c.idx];
          chain = c.end === 'end' ? piece.slice(0, -1).concat(chain) : piece.slice(1).reverse().concat(chain);
          used[c.idx] = true; extending = true; break;
        }
      }
      result.push({ pts: chain, labelName });
    }
  }
  return result;
}

function extractPolygonsMm(features, project, toMm) {
  const polygons = [];
  for (const feat of features) {
    const geom = feat.geometry;
    if (!geom) continue;
    const rings = geom.type === 'Polygon' ? [geom.coordinates]
                : geom.type === 'MultiPolygon' ? geom.coordinates : [];
    for (const poly of rings) {
      for (const ring of poly) {
        const projected = ring.map(([lon, lat]) => toMm(project(lat, lon)));
        if (projected.length >= 3) polygons.push(projected);
      }
    }
  }
  return polygons;
}

function extractLinesMm(features, project, toMm, classes) {
  const rawSegments = [];
  for (const feat of features) {
    const cls = feat.properties && feat.properties.class;
    if (!classes.includes(cls)) continue;
    const geom = feat.geometry;
    if (!geom) continue;
    const name = feat.properties && (feat.properties.name || feat.properties.ref);
    const lineArrays = geom.type === 'LineString' ? [geom.coordinates]
                     : geom.type === 'MultiLineString' ? geom.coordinates : [];
    for (const coords of lineArrays) {
      const projected = coords.map(([lon, lat]) => toMm(project(lat, lon)));
      if (projected.length >= 2) rawSegments.push({ pts: projected, name });
    }
  }
  // Chain same-named pieces that touch at intersections into longer
  // polylines first, THEN smooth -- this is what actually lets a road that
  // curves across several intersections read as one continuous curve
  // instead of a series of independently-preserved straight pieces.
  // For a very road-dense area (a big city), skip straight to the raw
  // (fast) lines instead -- streets there are short and packed close
  // enough that the curve is barely visible anyway, so it's not worth
  // the chaining + spline cost on potentially tens of thousands of segments.
  if (rawSegments.length > ROAD_SMOOTH_MAX_SEGMENTS) {
    return rawSegments.map(s => s.pts);
  }
  const chained = chainNamedSegments(rawSegments);
  const lines = [];
  for (const chain of chained) {
    const smoothed = chain.pts.length >= 3 ? tessellateSmoothOpenPath(chain.pts, 4) : chain.pts;
    if (smoothed.length >= 2) lines.push(smoothed);
  }
  return lines;
}

// Extract features from the VISIBLE MapLibre map and project them
function extractRawFeatures(map, widthKm, onStatus) {
  const center = map.getCenter();
  const lat = center.lat, lon = center.lng;
  const REF_SIZE = 500;
  const mmPerMeter = REF_SIZE / (widthKm * 1000);
  const project = makeProjector(lat, lon);
  // Apply map bearing rotation to projected mm coordinates so generated
  // layers match the rotated map view. mapBearing uses slider convention
  // (positive = visual right), MapLibre gets -mapBearing, so negate here too.
  const bearingRad = -(mapBearing || 0) * Math.PI / 180;
  const cosB = Math.cos(bearingRad), sinB = Math.sin(bearingRad);
  const toMm = (pt) => {
    const mx = pt.x * mmPerMeter, my = pt.y * mmPerMeter;
    return bearingRad ? { x: mx * cosB + my * sinB, y: -mx * sinB + my * cosB } : { x: mx, y: my };
  };

  if (onStatus) onStatus('Extracting features\u2026');
  const water = map.querySourceFeatures('openmaptiles', { sourceLayer: 'water' });
  const waterway = map.querySourceFeatures('openmaptiles', { sourceLayer: 'waterway' });
  const transportation = map.querySourceFeatures('openmaptiles', { sourceLayer: 'transportation' });
  const places = map.querySourceFeatures('openmaptiles', { sourceLayer: 'place' });

  const waterPolygons = extractPolygonsMm(water, project, toMm);
  const waterwayPolygons = extractPolygonsMm(waterway, project, toMm);

  // ── Spatial pre-filter ──────────────────────────────────────────────
  // The detail boost loads tiles well beyond the viewport. For lake-dense
  // areas (Manitoba, Lake of the Woods) this means thousands of polygons
  // outside the visible area get passed to ClipperLib union, which is the
  // dominant bottleneck. Filter them out early with a fast bbox overlap
  // check — this alone can cut polygon count by 80%+.
  const halfExtent = REF_SIZE * 0.85; // slightly larger than viewport to cover band offsets
  function bboxOverlapsViewport(ring) {
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const p of ring) {
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.y > maxY) maxY = p.y;
    }
    return maxX > -halfExtent && minX < halfExtent && maxY > -halfExtent && minY < halfExtent;
  }
  const nearbyWater = waterPolygons.filter(bboxOverlapsViewport);
  const nearbyWaterway = waterwayPolygons.filter(bboxOverlapsViewport);

  // ── Polygon simplification ──────────────────────────────────────────
  // Reduce vertex count before union. Vector tile polygons often have
  // excessive detail for the output resolution.
  function simplifyRing(ring, tol) {
    if (ring.length <= 6) return ring;
    const scaled = ring.map(p => ({ X: Math.round(p.x * CLIPPER_SCALE), Y: Math.round(p.y * CLIPPER_SCALE) }));
    const cleaned = ClipperLib.Clipper.CleanPolygon(scaled, tol * CLIPPER_SCALE);
    if (!cleaned || cleaned.length < 3) return ring;
    return cleaned.map(p => ({ x: p.X / CLIPPER_SCALE, y: p.Y / CLIPPER_SCALE }));
  }
  const SIMPLIFY_TOL = 0.25; // mm tolerance — invisible at output scale, reduces edge count significantly
  const simplifiedWater = nearbyWater.map(r => simplifyRing(r, SIMPLIFY_TOL));
  const simplifiedWaterway = nearbyWaterway.map(r => simplifyRing(r, SIMPLIFY_TOL));

  // ── Water body size filtering ────────────────────────────────────────
  // Our projection (Mercator, y-negated) preserves winding direction:
  //   • Outer rings → positive signed area (CW in screen coords)
  //   • Hole rings (island boundaries) → negative signed area (CCW)
  // ONLY outer rings are filtered by size/shape. Hole rings are ALWAYS
  // kept — filtering them removes islands from the map.
  // Elongated slivers (high aspect ratio + small area) are discarded
  // to eliminate dash artefacts from fragmented vector tile data.
  function signedPolyArea(ring) {
    let a = 0;
    for (let i = 0, n = ring.length; i < n; i++) {
      const j = (i + 1) % n;
      a += ring[i].x * ring[j].y - ring[j].x * ring[i].y;
    }
    return a / 2;
  }
  function isSliver(ring, area) {
    if (area > 120) return false;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const p of ring) {
      if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y;
    }
    const w = maxX - minX, h = maxY - minY;
    if (w < 0.01 || h < 0.01) return true;
    return Math.max(w / h, h / w) > 4;
  }
  const DISCARD_THRESHOLD = 16;  // mm²
  const SMALL_LAKE_THRESHOLD = 80; // mm²
  const allWaterPolys = [...simplifiedWater, ...simplifiedWaterway];

  // Split into outer rings (water bodies) and hole rings (islands)
  const holeRings = [];   // always kept
  const outerRings = [];  // filtered by size + shape
  for (const ring of allWaterPolys) {
    const sa = signedPolyArea(ring);
    if (sa < 0) { holeRings.push(ring); }   // island boundary — never discard
    else { outerRings.push(ring); }
  }
  // Filter outer rings only — holes pass through untouched
  const filteredOuters = outerRings.filter(ring => {
    const area = signedPolyArea(ring); // positive for outers
    if (area < DISCARD_THRESHOLD) return false;
    if (isSliver(ring, area)) return false;
    return true;
  });
  const viablePolys = [...filteredOuters, ...holeRings];
  const bigWaterPolys = [...filteredOuters.filter(r => signedPolyArea(r) >= SMALL_LAKE_THRESHOLD), ...holeRings];
  const smallLakePolys = filteredOuters.filter(r => {
    const a = signedPolyArea(r);
    return a >= DISCARD_THRESHOLD && a < SMALL_LAKE_THRESHOLD;
  });
  const filteredWaterPolygons = showTinyLakes ? viablePolys : bigWaterPolys;
  // Waterway features are LineStrings (centerlines), not polygons. Buffer
  // them into visible water shapes. Width varies by class.
  // Also spatial-filter: skip lines entirely outside the viewport.
  const waterwayLines = [];
  for (const feat of waterway) {
    const geom = feat.geometry;
    if (!geom) continue;
    const cls = feat.properties && feat.properties.class;
    const lineArrays = geom.type === 'LineString' ? [geom.coordinates]
                     : geom.type === 'MultiLineString' ? geom.coordinates : [];
    for (const coords of lineArrays) {
      const projected = coords.map(([lon, lat]) => toMm(project(lat, lon)));
      if (projected.length < 2) continue;
      // Quick viewport check: skip if all points are outside
      const inView = projected.some(p => Math.abs(p.x) < halfExtent && Math.abs(p.y) < halfExtent);
      if (!inView) continue;
      // Skip very short segments — they buffer into tiny dashes that look
      // like artefacts and won't cut well. Compute total polyline length.
      let lineLen = 0;
      for (let i = 1; i < projected.length; i++)
        lineLen += Math.hypot(projected[i].x - projected[i-1].x, projected[i].y - projected[i-1].y);
      if (lineLen < 6) continue; // mm — skip anything shorter than ~6mm
      waterwayLines.push({ pts: projected, cls });
    }
  }
  const riverLines = waterwayLines.filter(l => l.cls === 'river').map(l => l.pts);
  const streamLines = waterwayLines.filter(l => l.cls !== 'river').map(l => l.pts);
  const riverPolysRaw = riverLines.length ? bufferLinesToPolygons(riverLines, 1.5) : [];
  const streamPolys = streamLines.length ? bufferLinesToPolygons(streamLines, 0.6) : [];
  // Filter buffered river polys — remove slivers and tiny fragments
  const riverPolys = riverPolysRaw.filter(ring => {
    const area = Math.abs(signedPolyArea(ring));
    return area >= DISCARD_THRESHOLD && !isSliver(ring, area);
  });
  // Skip the expensive unionPolygons for water assembly. ClipperLib's
  // NonZero fill rule in computeBands' intersect/difference operations
  // handles overlapping polygons correctly — the union was redundant work.
  // For lake-dense areas (Manitoba, Lake of the Woods) this cuts generation
  // time dramatically by avoiding a union of thousands of polygons.
  const waterBase = [...filteredWaterPolygons, ...riverPolys];
  const allWaterRaw = showStreams ? [...waterBase, ...streamPolys] : waterBase;
  const majorRoadsMm = extractLinesMm(transportation, project, toMm, MAJOR_ROAD_CLASSES);
  const minorRoadsMm = extractLinesMm(transportation, project, toMm, MINOR_ROAD_CLASSES);

  // Extract name/ref POINTS from transportation_name now (while tiles are
  // loaded at boosted zoom) but defer the expensive chain MATCHING to
  // ensureRoadNameLines() which runs lazily when labels are first turned on.
  const transportationName = map.querySourceFeatures('openmaptiles', { sourceLayer: 'transportation_name' });
  const majorNamePoints = [], tertiaryNamePoints = [];
  const seenNameCoords = new Set();
  for (const feat of transportationName) {
    const cls = feat.properties && feat.properties.class;
    if (!LABEL_ROAD_CLASSES.includes(cls)) continue;
    const name = feat.properties && feat.properties.name;
    const ref = feat.properties && feat.properties.ref;
    if (!name && !ref) continue;
    const geom = feat.geometry;
    if (!geom) continue;
    const coords = geom.type === 'LineString' ? geom.coordinates
                 : geom.type === 'MultiLineString' ? geom.coordinates[0] : null;
    if (!coords || coords.length < 2) continue;
    const midIdx = Math.floor(coords.length / 2);
    const pt = toMm(project(coords[midIdx][1], coords[midIdx][0]));
    // Road direction at midpoint for direction-aware matching
    const i0 = Math.max(0, midIdx - 1), i1 = Math.min(coords.length - 1, midIdx + 1);
    const pA = toMm(project(coords[i0][1], coords[i0][0]));
    const pB = toMm(project(coords[i1][1], coords[i1][0]));
    const dLen = Math.hypot(pB.x - pA.x, pB.y - pA.y) || 1;
    const dir = { dx: (pB.x - pA.x) / dLen, dy: (pB.y - pA.y) / dLen };
    const isTertiary = cls === 'tertiary' || cls === 'minor';
    const targetList = isTertiary ? tertiaryNamePoints : majorNamePoints;
    if (name) {
      const ck = 'n|' + name + '|' + Math.round(pt.x / 3) + ',' + Math.round(pt.y / 3);
      if (!seenNameCoords.has(ck)) { seenNameCoords.add(ck); targetList.push({ text: name, x: pt.x, y: pt.y, isRef: false, dir }); }
    }
    if (ref && !isTertiary) {
      const ck = 'r|' + ref + '|' + Math.round(pt.x / 3) + ',' + Math.round(pt.y / 3);
      if (!seenNameCoords.has(ck)) { seenNameCoords.add(ck); majorNamePoints.push({ text: ref, x: pt.x, y: pt.y, isRef: true, dir }); }
    }
  }
  const roadNameLines = null; // null = matching not yet done; [] = done but empty

  const labelsMm = [];
  const seenLabels = new Set();
  for (const feat of places) {
    const name = feat.properties && feat.properties.name;
    const cls = feat.properties && feat.properties.class;
    if (!name || seenLabels.has(name)) continue;
    seenLabels.add(name);
    const geom = feat.geometry;
    if (!geom || geom.type !== 'Point') continue;
    const [lon2, lat2] = geom.coordinates;
    const pt = toMm(project(lat2, lon2));
    const rank = cls === 'city' ? 1 : cls === 'town' ? 2 : cls === 'village' ? 3 : cls === 'suburb' ? 4 : 5;
    labelsMm.push({ name, x: pt.x, y: pt.y, rank, cls });
  }
  labelsMm.sort((a, b) => a.rank - b.rank);

  const margin = 1.5;
  const cacheHalf = (widthKm * margin * 1000 * mmPerMeter) / 2;

  return { lat, lon, mmPerMeter, allWaterRaw, waterBase, streamPolys, bigWaterPolys, smallLakePolys, riverPolys, majorRoadsMm, minorRoadsMm, roadNameLines, majorNamePoints, tertiaryNamePoints, labelsMm, cacheHalfMm: cacheHalf, refSize: REF_SIZE, widthKm };
}

// Phase 2: compute smoothed land + bands from cached raw features
function computeBands(rawCache, onStatus, aspect) {
  aspect = (aspect && isFinite(aspect) && aspect > 0) ? aspect : 1;
  const { allWaterRaw, cacheHalfMm: cacheHalf, refSize: REF_SIZE } = rawCache;
  if (onStatus) onStatus('Smoothing coastlines…');
  const refAspect = 1; // will be applied in buildLayers
  // Use a rect sized to cover the viewport + band offsets, NOT the full cache.
  // This prevents land blocks from appearing at data-edge boundaries.
  const maxBandOffset = (BAND_1_MM + BAND_2_MM + BAND_3_MM + BAND_4_MM + BAND_5_MM) * 2;
  // Per-axis clipping: Y must scale with aspect ratio so tall canvases don't
  // get water layers cut off at the bottom. Without this, a portrait map uses
  // a square compute rect that's too short to cover the full viewport height.
  const viewHalfX = REF_SIZE * 0.75 + maxBandOffset;
  const viewHalfY = Math.max(viewHalfX, REF_SIZE * aspect * 0.75 + maxBandOffset);
  const landComputeRect = [[ { x: -viewHalfX, y: -viewHalfY }, { x: viewHalfX, y: -viewHalfY }, { x: viewHalfX, y: viewHalfY }, { x: -viewHalfX, y: viewHalfY } ]];
  const allWaterClipped = intersectPolygons(allWaterRaw, landComputeRect);
  let fullLand = differencePolygons(landComputeRect, allWaterClipped);

  // Simplify for large areas
  const simplifyTol = Math.max(0.1, REF_SIZE / 2000);
  if (simplifyTol > 0.2) {
    const scaled = fullLand.map(r => r.map(p => ({X: Math.round(p.x * CLIPPER_SCALE), Y: Math.round(p.y * CLIPPER_SCALE)})));
    const cleaned = ClipperLib.Clipper.CleanPolygons(scaled, simplifyTol * CLIPPER_SCALE);
    fullLand = cleaned.map(r => r.map(p => ({x: p.X / CLIPPER_SCALE, y: p.Y / CLIPPER_SCALE}))).filter(r => r.length >= 3);
  }

  let smoothedLandFull = fullLand;
  smoothedLandFull = cleanupPolygons(offsetPolygons(offsetPolygons(smoothedLandFull, SHALLOW_SMOOTH_MM), -SHALLOW_SMOOTH_MM));
  if (REF_SIZE < 400) { // always true at REF_SIZE=500
    smoothedLandFull = cleanupPolygons(offsetPolygons(offsetPolygons(smoothedLandFull, SHALLOW_SMOOTH_MM * 0.6), -SHALLOW_SMOOTH_MM * 0.6));
  }

  // For bands 4-5 ONLY, clip the land source to the actual output viewport.
  // Bands 1-3 continue to use the full compute-rect land source (unchanged
  // behavior). Bands 4-5 use viewport-clipped land so that any phantom "land"
  // in the compute-rect margins (from OSM tile data ending inside the compute
  // rect) can't contribute band offsets that reach into viewport water.
  const vpHalfX = REF_SIZE / 2;
  const vpHalfY = REF_SIZE * aspect / 2;
  const viewportRect = [[
    { x: -vpHalfX, y: -vpHalfY },
    { x:  vpHalfX, y: -vpHalfY },
    { x:  vpHalfX, y:  vpHalfY },
    { x: -vpHalfX, y:  vpHalfY }
  ]];
  const viewportLand = intersectPolygons(smoothedLandFull, viewportRect);

  if (onStatus) onStatus('Computing depth contours…');
  const mapScale = REF_SIZE;
  const preMaxGap = Math.max(0.8, REF_SIZE / 220);
  const smoothRad = Math.max(4, SHALLOW_SMOOTH_MM * 0.6) * Math.max(1, bandScale);
  const tessSegs = 5;

  // Inner boundary: bands must not extend beyond this to avoid data-edge artifacts
  const bandClipRect = [[ {x:-viewHalfX,y:-viewHalfY}, {x:viewHalfX,y:-viewHalfY}, {x:viewHalfX,y:viewHalfY}, {x:-viewHalfX,y:viewHalfY} ]];

  function makeBandFull(source, totalOffset, noiseAmp, seed, noiseScale) {
    let raw = intersectPolygons(offsetPolygons(source, totalOffset), bandClipRect);
    // Double smoothing pass to round off all angular features
    raw = cleanupPolygons(offsetPolygons(offsetPolygons(raw, smoothRad), -smoothRad));
    raw = cleanupPolygons(offsetPolygons(offsetPolygons(raw, smoothRad * 0.6), -smoothRad * 0.6));
    // Apply different noise to coastal vs enclosed (lake) rings
    const resampled = raw.map(r => resampleRing(r, preMaxGap));
    const displaced = cleanupPolygons(resampled.map(ring => {
      // Check if this ring is near the cache boundary (coastal) or interior (lake)
      const nearEdge = ring.some(p => Math.abs(p.x) > viewHalfX * 0.7 || Math.abs(p.y) > viewHalfY * 0.7);
      const amp = nearEdge ? noiseAmp * 1.5 : noiseAmp * 0.25; // lakes get gentler noise
      const sc = nearEdge ? noiseScale : noiseScale * 1.5; // lakes get broader features
      return displaceRingWithNoise(ring, amp, seed, sc);
    }));
    // Double smoothing AFTER noise to eliminate all sharp edges
    let smoothed = cleanupPolygons(offsetPolygons(offsetPolygons(displaced, smoothRad * 0.6), -smoothRad * 0.6));
    smoothed = cleanupPolygons(offsetPolygons(offsetPolygons(smoothed, smoothRad * 0.3), -smoothRad * 0.3));
    // Three-step organic smoothing:
    // 1. Chaikin rounds all angles into smooth curves
    // 2. Low-frequency noise adds flowing artistic deviation
    // 3. Second Chaikin pass smooths any noise edges
    smoothed = chaikinSmoothPolygons(smoothed, 2);
    // Gentle wave deviation (subtle, broad features only)
    const waveAmp = totalOffset * 0.15;
    const waveScale = noiseScale * 3;
    smoothed = smoothed.map(ring => {
      const nearEdge = ring.some(p => Math.abs(p.x) > viewHalfX * 0.7 || Math.abs(p.y) > viewHalfY * 0.7);
      const a = nearEdge ? waveAmp : waveAmp * 0.1;
      return displaceRingWithNoise(ring, a, seed + 5000, waveScale);
    });
    smoothed = cleanupPolygons(smoothed);
    smoothed = chaikinSmoothPolygons(smoothed, 2);
    // Strip tiny fragment rings produced by offset/smoothing at sharp
    // corners or near-tangent edges. These show up as specks on the bands.
    const MIN_BAND_RING_AREA = 8; // mm²
    smoothed = smoothed.filter(ring => {
      let a = 0;
      for (let i = 0, n = ring.length; i < n; i++) {
        const j = (i + 1) % n;
        a += ring[i].x * ring[j].y - ring[j].x * ring[i].y;
      }
      return Math.abs(a) / 2 >= MIN_BAND_RING_AREA;
    });
    return smoothed;
  }

  const b1 = BAND_1_MM * bandScale, b2 = BAND_2_MM * bandScale, b3 = BAND_3_MM * bandScale;
  const b4 = BAND_4_MM * bandScale, b5 = BAND_5_MM * bandScale;
  const band1Tess = makeBandFull(smoothedLandFull, b1, b1 * 0.8, 42, mapScale * 0.15);
  const band2Tess = makeBandFull(smoothedLandFull, b1 + b2, b2 * 1.2, 571, mapScale * 0.22);
  const band3Tess = makeBandFull(smoothedLandFull, b1 + b2 + b3, b3 * 1.0, 1847, mapScale * 0.35);
  const band4Tess = makeBandFull(viewportLand, b1 + b2 + b3 + b4, b4 * 1.0, 2953, mapScale * 0.45);
  const band5Tess = makeBandFull(viewportLand, b1 + b2 + b3 + b4 + b5, b5 * 0.9, 4111, mapScale * 0.55);

  return { ...rawCache, band1Tess, band2Tess, band3Tess, band4Tess, band5Tess };
}

// Convenience: extract + compute in one call
function extractAndProject(map, widthKm, widthMm, onStatus) {
  const raw = extractRawFeatures(map, widthKm, onStatus);
  return computeBands(raw, onStatus);
}

// Build layers from cached data for a specific viewport
// Lazy road-name matching: takes the name points extracted during generate
// (from transportation_name, while boosted tiles were loaded) and matches
// them to road chains by spatial proximity. Only runs once, when labels
// are first turned on.
function ensureRoadNameLines(cache) {
  if (cache.roadNameLines !== null) return; // already computed
  const result = [];
  function matchChains(chains, namePoints, threshold, isMajor) {
    if (!namePoints || !namePoints.length || !chains.length) return;
    const gridSz = threshold * 2;
    const grid = new Map();
    for (const np of namePoints) {
      const k = Math.floor(np.x / gridSz) + ',' + Math.floor(np.y / gridSz);
      if (!grid.has(k)) grid.set(k, []);
      grid.get(k).push(np);
    }
    for (const chain of chains) {
      let bestName = null, bestRef = null, bestNameDist = Infinity, bestRefDist = Infinity;
      const step = Math.max(1, Math.floor(chain.length / 30));
      for (let ci = 0; ci < chain.length; ci += step) {
        const ci0 = Math.max(0, ci - 1), ci1 = Math.min(chain.length - 1, ci + 1);
        const cdx = chain[ci1].x - chain[ci0].x, cdy = chain[ci1].y - chain[ci0].y;
        const clen = Math.hypot(cdx, cdy) || 1;
        const cnx = cdx / clen, cny = cdy / clen;
        const gx = Math.floor(chain[ci].x / gridSz), gy = Math.floor(chain[ci].y / gridSz);
        for (let dx = -1; dx <= 1; dx++) for (let dy = -1; dy <= 1; dy++) {
          const pts = grid.get((gx+dx)+','+(gy+dy));
          if (!pts) continue;
          for (const np of pts) {
            const d = Math.hypot(chain[ci].x - np.x, chain[ci].y - np.y);
            if (np.dir) {
              const dot = Math.abs(np.dir.dx * cnx + np.dir.dy * cny);
              if (dot < 0.3) continue;
            }
            if (!np.isRef && d < bestNameDist) { bestNameDist = d; bestName = np.text; }
            if (np.isRef && d < bestRefDist) { bestRefDist = d; bestRef = np.text; }
          }
        }
      }
      if (bestName && bestNameDist < threshold) result.push({ pts: chain, name: bestName, isRef: false, isMajor });
      else if (bestRef && bestRefDist < threshold) result.push({ pts: chain, name: bestRef, isRef: true, isMajor });
    }
  }
  matchChains(cache.majorRoadsMm, cache.majorNamePoints, 10, true);
  matchChains(cache.minorRoadsMm, cache.tertiaryNamePoints || [], 10, false);
  cache.roadNameLines = result;
  console.log('[road names] matched:', result.length, 'lines');
}

function buildLayers(cache, widthMm, heightMm, panXmm, panYmm, currentRoadMode, computeExportRoads) {
  const aspect = heightMm / widthMm;
  const halfW = cache.refSize / 2, halfH = (cache.refSize * aspect) / 2;
  const landRect = [[
    { x: panXmm - halfW, y: panYmm - halfH }, { x: panXmm + halfW, y: panYmm - halfH },
    { x: panXmm + halfW, y: panYmm + halfH }, { x: panXmm - halfW, y: panYmm + halfH },
  ]];

  const waterUnion = cleanupPolygons(intersectPolygons(cache.allWaterRaw, landRect));
  const land = differencePolygons(landRect, waterUnion);
  const waterClipped = intersectPolygons(waterUnion, landRect);

  const shallowHoles = enabledBands[1] ? filterTinyPolygons(differencePolygons(waterClipped, intersectPolygons(cache.band1Tess, landRect)), 3) : [];
  const midHoles     = enabledBands[2] ? filterTinyPolygons(differencePolygons(waterClipped, intersectPolygons(cache.band2Tess, landRect)), 3) : [];
  const outerHoles   = enabledBands[3] ? filterTinyPolygons(differencePolygons(waterClipped, intersectPolygons(cache.band3Tess, landRect)), 3) : [];
  const band4Holes   = enabledBands[4] ? filterTinyPolygons(differencePolygons(waterClipped, intersectPolygons(cache.band4Tess, landRect)), 3) : [];
  const band5Holes   = enabledBands[5] ? filterTinyPolygons(differencePolygons(waterClipped, intersectPolygons(cache.band5Tess, landRect)), 3) : [];

  // allRoadPolys (buffered/filled road shapes) is ONLY consumed by the export
  // panel (buildLayeredExportSvg) -- the preview draws roads as cheap stroked
  // lines instead (see majorLines/minorLines below), never touching this.
  // Computing it is the single most expensive step here (ClipperLib buffer +
  // several union/intersect/difference passes over every road segment), so
  // it's skipped entirely unless the caller is actually about to export --
  // otherwise every ordinary Generate/settings-change was paying this cost
  // for a result nothing ever looked at, which is what made dense areas
  // (a whole city, a country) painfully slow to just preview.
  let allRoadPolys = [];
  if (computeExportRoads && currentRoadMode !== 'off') {
    const majorPolys = intersectPolygons(bufferLinesToPolygons(cache.majorRoadsMm, 0.5 * roadThickMult), landRect);
    allRoadPolys = majorPolys;
    if (currentRoadMode === 'all') {
      const minorPolys = intersectPolygons(bufferLinesToPolygons(cache.minorRoadsMm, 0.22 * roadThickMult), landRect);
      allRoadPolys = unionPolygons([...majorPolys, ...minorPolys]);
    }
  }

  const visibleLabels = cache.labelsMm.filter(l => {
    if (l.x < panXmm - halfW || l.x > panXmm + halfW) return false;
    if (l.y < panYmm - halfH || l.y > panYmm + halfH) return false;
    // Check multiple points around the label to ensure it doesn't hang over water
    const labelW = l.name.length * 2.5; // generous buffer to keep labels well inland // approximate half-width in ref units
    // Also check that the full label text fits within the viewport
    if (l.x - labelW < panXmm - halfW + 2 || l.x + labelW > panXmm + halfW - 2) return false;
    if (l.y < panYmm - halfH + 4 || l.y > panYmm + halfH - 4) return false;
    const testPoints = [{x:l.x, y:l.y}, {x:l.x-labelW, y:l.y}, {x:l.x+labelW, y:l.y}];
    for (const tp of testPoints) {
      let crossings = 0;
      for (const ring of waterUnion) {
        for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
          const xi = ring[i].x, yi = ring[i].y, xj = ring[j].x, yj = ring[j].y;
          if ((yi > tp.y) !== (yj > tp.y) && tp.x < ((xj - xi) * (tp.y - yi)) / (yj - yi) + xi) crossings++;
        }
      }
      if (crossings % 2 !== 0) return false; // any test point in water = skip
    }
    return true;
  });

  // Frame: merge into each layer, clip roads to inner area
  const ft = showFrame ? Math.min(halfW*2, halfH*2) * frameThickness : 0;
  let innerClipRect = landRect;
  if (ft > 0) {
    innerClipRect = [[
      { x: panXmm - halfW + ft, y: panYmm - halfH + ft },
      { x: panXmm + halfW - ft, y: panYmm - halfH + ft },
      { x: panXmm + halfW - ft, y: panYmm + halfH - ft },
      { x: panXmm - halfW + ft, y: panYmm + halfH - ft },
    ]];
    // Clip roads to inside the frame
    allRoadPolys = allRoadPolys.length ? intersectPolygons(allRoadPolys, innerClipRect) : [];
  }

  // Compass backer: circle polygon merged with land, subtracted from roads
  let compassBacker = [];
  if (compassCorner !== 'off') {
    const sz = Math.min(halfW*2, halfH*2) * 0.12 * compassSzMult;
    const margin = sz * 0.3;
    const r = sz * 0.56;
    let cx, cy;
    if (compassCorner === 'tl') { cx = panXmm - halfW + margin + sz/2; cy = panYmm - halfH + margin + sz/2; }
    else if (compassCorner === 'tr') { cx = panXmm + halfW - margin - sz/2; cy = panYmm - halfH + margin + sz/2; }
    else if (compassCorner === 'bl') { cx = panXmm - halfW + margin + sz/2; cy = panYmm + halfH - margin - sz/2; }
    else { cx = panXmm + halfW - margin - sz/2; cy = panYmm + halfH - margin - sz/2; }
    cx += compassOffX; cy += compassOffY;
    // Create circle polygon (32 segments)
    const circPts = [];
    for (let i = 0; i < 32; i++) {
      const a = (i / 32) * Math.PI * 2;
      circPts.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
    }
    compassBacker = [circPts];
    compassBacker._cx = cx; compassBacker._cy = cy; compassBacker._r = r;
    // Subtract compass area from roads (knock out)
    if (allRoadPolys.length) allRoadPolys = differencePolygons(allRoadPolys, compassBacker);
  }

  // Label backers: compute them as polygons for merging
  const labelBackerPolys = [];
  if (showLabel && visibleLabels.length) {
    const mapSize = Math.min(halfW * 2, halfH * 2);
    const placed2 = [];
    for (const l of visibleLabels) {
      const sz = (l.rank <= 1 ? mapSize * 0.025 : l.rank <= 2 ? mapSize * 0.018 : l.rank <= 3 ? mapSize * 0.013 : mapSize * 0.01) * labelSzMult;
      if (sz < 1.5) continue;
      // Skip labels that fall inside the compass backer circle
      if (compassBacker._r && Math.hypot(l.x - compassBacker._cx, l.y - compassBacker._cy) < compassBacker._r * 1.1) continue;
      const minDist = sz * l.name.length * 0.45;
      const tooClose = placed2.some(p => Math.hypot(p.x - l.x, p.y - l.y) < Math.max(minDist, p.minDist));
      if (tooClose) continue;
      placed2.push({ x: l.x, y: l.y, minDist, sz, name: l.name });
      const tw = sz * l.name.length * 0.52, pd = sz * 0.35, rh = sz * 1.4;
      const rx = l.x - tw/2 - pd, ry = l.y - rh/2;
      labelBackerPolys.push([{x:rx,y:ry},{x:rx+tw+pd*2,y:ry},{x:rx+tw+pd*2,y:ry+rh},{x:rx,y:ry+rh}]);
    }
    // Subtract label backers from roads
    if (allRoadPolys.length && labelBackerPolys.length) {
      allRoadPolys = differencePolygons(allRoadPolys, labelBackerPolys);
    }
  }

  // Road name labels: decide which major road chains get a curved street
  // name, using the exact same chained + spline-smoothed polyline the road
  // itself is drawn from, so the label always follows the road's own visible
  // curve. Only the DECISION is made here (which chains qualify, where the
  // label sits, which direction reads upright) -- no font is loaded at this
  // point, so fit is checked with a DOM text measurement, the same way the
  // rest of this function sizes things before opentype.js gets involved at
  // export time. Both the preview (native SVG textPath) and the export
  // (opentype.js per-letter placement) read this SAME array below, so they
  // always agree with each other and with the knockout cut into the roads
  // right after this.
  const roadLabels = [];
  if (showRoadLabels && currentRoadMode !== 'off') {
    ensureRoadNameLines(cache);
  }
  if (showRoadLabels && currentRoadMode !== 'off' && cache.roadNameLines && cache.roadNameLines.length) {
    const mapSize3 = Math.min(halfW * 2, halfH * 2);
    const roadLabelSz = mapSize3 * 0.015 * roadLabelSzMult;
    if (roadLabelSz >= 1.2) {
      // Deduplicate: transportation_name returns many short segments for the
      // same road name. Group by name and keep only the longest in-frame
      // segment so each name appears ONCE on the map.
      const bestByName = new Map();
      for (const chain of cache.roadNameLines) {
        if (!chain.name || chain.pts.length < 2) continue;
        const inFrame = chain.pts.some(p =>
          p.x >= panXmm - halfW && p.x <= panXmm + halfW &&
          p.y >= panYmm - halfH && p.y <= panYmm + halfH);
        if (!inFrame) continue;

        let pts = chain.pts;
        if (pts[pts.length - 1].x - pts[0].x < 0) pts = pts.slice().reverse();

        let total = 0;
        for (let i = 1; i < pts.length; i++) total += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);

        const rawTextW = measureTextWidth(chain.name, 'Inter,Helvetica,sans-serif', roadLabelSz, '700');
        const textW = rawTextW + chain.name.length * roadLabelSz * 0.02;
        if (textW > total) continue;

        // Refs and names use separate dedup keys so a road can have both
        const dedupKey = (chain.isRef ? 'ref:' : 'name:') + chain.name;
        const prev = bestByName.get(dedupKey);
        if (!prev || total > prev.total) {
          bestByName.set(dedupKey, { name: chain.name, pts, sz: roadLabelSz, textW, total, isRef: !!chain.isRef, isMajor: chain.isMajor !== false });
        }
      }

      // Place labels near the CENTER of the viewport. For each road, sample
      // positions along it and pick the one closest to the viewport center
      // where the full text span fits within the map edges. Simple, predictable,
      // and gives the result users expect — labels in the middle of the map.
      const vpCx = panXmm, vpCy = panYmm; // viewport center
      const edgeMargin = roadLabelSz * 0.5;
      const placedRoadAnchors = [];

      function walkTo(pts, total, targetS) {
        targetS = Math.max(0, Math.min(total, targetS));
        let a = 0;
        for (let i = 1; i < pts.length; i++) {
          const sl = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
          if (a + sl >= targetS) {
            const t = sl ? (targetS - a) / sl : 0;
            return { x: pts[i - 1].x + (pts[i].x - pts[i - 1].x) * t,
                     y: pts[i - 1].y + (pts[i].y - pts[i - 1].y) * t };
          }
          a += sl;
        }
        return { x: pts[pts.length - 1].x, y: pts[pts.length - 1].y };
      }

      function inViewport(p) {
        return p.x >= panXmm - halfW + edgeMargin && p.x <= panXmm + halfW - edgeMargin &&
               p.y >= panYmm - halfH + edgeMargin && p.y <= panYmm + halfH - edgeMargin;
      }




      for (const entry of bestByName.values()) {
        const textHalf = entry.textW / 2;
        let bestS = entry.total / 2, bestDistToCenter = Infinity, found = false;

        // Sample 30 positions along the road
        for (let ci = 0; ci < 30; ci++) {
          const s = textHalf + ((entry.total - entry.textW) * ci / 29) || entry.total / 2;
          // Check that both text endpoints fit in viewport
          const pS = walkTo(entry.pts, entry.total, s - textHalf);
          const pE = walkTo(entry.pts, entry.total, s + textHalf);
          if (!inViewport(pS) || !inViewport(pE)) continue;
          // Position of the label center
          const pC = walkTo(entry.pts, entry.total, s);
          const d = Math.hypot(pC.x - vpCx, pC.y - vpCy);
          if (d < bestDistToCenter) { bestDistToCenter = d; bestS = s; found = true; }
        }
        if (!found) continue;

        const anchor = walkTo(entry.pts, entry.total, bestS);
        const minDist = Math.max(entry.textW * 0.4, roadLabelSz * 2);
        const tooClose = placedRoadAnchors.some(a => Math.hypot(a.x - anchor.x, a.y - anchor.y) < minDist);
        if (tooClose) continue;
        placedRoadAnchors.push({ x: anchor.x, y: anchor.y });

        roadLabels.push({ name: entry.name, pts: entry.pts, sz: roadLabelSz, textW: entry.textW, anchorS: bestS, total: entry.total, isRef: entry.isRef, isMajor: entry.isMajor });
      }
    }
    const nameCount = roadLabels.filter(r => !r.isRef).length;
    const refCount = roadLabels.filter(r => r.isRef).length;
    console.log('[road labels]', cache.roadNameLines.length, 'matched road lines (' + cache.roadNameLines.filter(r=>!r.isRef).length + ' names, ' + cache.roadNameLines.filter(r=>r.isRef).length + ' refs) →', roadLabels.length, 'placed (' + nameCount + ' names, ' + refCount + ' refs, sz=' + roadLabelSz.toFixed(2) + ')');
  }

  // Road label knockouts are handled in buildLayeredExportSvg using the
  // actual rendered label polygons — no orphaned gaps.

  // Backer merge into land: only for EXPORT builds (computeExportRoads=true).
  // For preview, the overlay rects in labelSvg/compassSvg handle the visual
  // backers, and skipping these expensive unionPolygons calls is a significant
  // speedup on complex maps. It also prevents "ghost backers" — rectangles
  // baked into the land shape that persist when labels are toggled off.
  let mergedLand = land;
  if (computeExportRoads) {
    if (compassBacker.length) {
      const normalized = offsetPolygons(compassBacker, 0.01);
      if (normalized.length) mergedLand = unionPolygons([...mergedLand, ...normalized]);
    }
    if (labelBackerPolys.length) {
      const normalized = offsetPolygons(labelBackerPolys, 0.01);
      if (normalized.length) mergedLand = unionPolygons([...mergedLand, ...normalized]);
    }
  }

  return { shallowHoles, midHoles, outerHoles, band4Holes, band5Holes, land: mergedLand, allRoadPolys, visibleLabels,
           majorLines: cache.majorRoadsMm, minorLines: cache.minorRoadsMm, roadLabels,
           panXmm, panYmm, halfW, halfH, compassBacker, labelBackerPolys, ft, innerClipRect };
}

/* =========================================================================
   SVG EXPORT
   ========================================================================= */

function polygonsToPathD(polygons) {
  return polygons.map((ring) => {
    const pts = ring.filter(p => isFinite(p.x) && isFinite(p.y));
    if (pts.length < 3) return '';
    return pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ') + ' Z';
  }).filter(Boolean).join(' ');
}

function rectPathD(w, h, px, py) {
  const hW = w/2, hH = h/2;
  return `M ${px-hW},${py-hH} L ${px+hW},${py-hH} L ${px+hW},${py+hH} L ${px-hW},${py+hH} Z`;
}

function bandPathD(holes, w, h, px, py) {
  const rect = rectPathD(w, h, px, py);
  const holePaths = polygonsToPathD(holes);
  return holePaths ? `${rect} ${holePaths}` : rect;
}

// Frame: outer rect with inner rect hole, creating a border
function frameSvg(w, h, px, py, thickness, color) {
  if (!showFrame || !thickness) return '';
  const hW = w/2, hH = h/2, t = thickness;
  const outer = `M ${px-hW},${py-hH} L ${px+hW},${py-hH} L ${px+hW},${py+hH} L ${px-hW},${py+hH} Z`;
  const inner = `M ${px-hW+t},${py-hH+t} L ${px+hW-t},${py-hH+t} L ${px+hW-t},${py+hH-t} L ${px-hW+t},${py+hH-t} Z`;
  return `<path d="${outer} ${inner}" fill="${color || '#c8c0ab'}" fill-rule="evenodd"/>`;
}

function innerRect(w, h, px, py, thickness) {
  const hW = w/2, hH = h/2, t = showFrame ? thickness : 0;
  return { x1: px-hW+t, y1: py-hH+t, x2: px+hW-t, y2: py+hH-t };
}

// Compass rose
const COMPASS_SVG = `<path d="M338.659,123.74c-27.17-27.021-60.29-41.128-98.528-43.504c-2.452-10.842-4.85-21.448-7.249-32.055
 c-0.222,0.028-0.445,0.055-0.667,0.083c-2.374,10.681-4.748,21.361-7.127,32.064c-38.043,2.742-70.697,16.983-97.495,43.817
 c-26.859,26.895-41.102,59.644-43.703,97.641c-10.902,2.389-21.623,4.738-32.345,7.087c0.026,0.239,0.052,0.478,0.077,0.717
 c10.782,2.387,21.565,4.773,32.233,7.134c2.466,38.474,16.666,71.355,43.564,98.372c26.957,27.076,59.87,41.421,98.096,43.993
 c2.396,10.84,4.764,21.554,7.132,32.268c0.188,0.024,0.376,0.048,0.564,0.072c2.383-10.761,4.767-21.523,7.157-32.314
 c38.448-2.378,71.498-16.666,98.593-43.748c27.153-27.139,41.494-60.269,43.799-98.676c10.996-2.42,21.712-4.777,32.428-7.135
 c0.011-0.218,0.022-0.436,0.033-0.654c-10.784-2.336-21.569-4.672-32.504-7.041C380.166,183.571,365.753,150.685,338.659,123.74z
  M223.015,88.637c-0.02,2.135-0.571,3.957-1.202,5.999c-32.839,3.17-61.167,16.134-84.431,39.463
 c-23.208,23.271-36.051,51.563-39.221,84.334c-1.95,0.697-3.842,1.255-5.888,1.205C94.837,153.072,152.639,92.23,223.015,88.637z
  M150.727,146.175c-0.206,0.173-0.412,0.346-0.618,0.519c11.285,17.555,22.57,35.111,34.171,53.159
 c-24.041,5.25-47.646,10.404-71.883,15.696c3.448-26.513,13.79-49.235,31.624-68.412c20.135-21.651,44.975-34.29,74.814-38.259
 c-5.29,23.976-10.469,47.446-15.753,71.394C185.345,168.721,168.036,157.448,150.727,146.175z M202.699,182.93
 c-0.803,4.392-1.587,8.678-2.483,13.576c-13.799-13.556-27.135-26.658-40.471-39.759c0.127-0.168,0.254-0.336,0.381-0.504
 C174.242,165.091,188.359,173.94,202.699,182.93z M75.205,229.307c-0.997,0-1.993,0-2.99,0c-0.035-0.265-0.07-0.53-0.104-0.796
 c43.394-8.602,86.788-17.205,130.212-25.813c6.598,5.77,13.066,11.428,19.535,17.084c2.855,2.497,5.714,4.99,8.568,7.488
 c0.737,0.645,1.561,1.244,1.46,2.381c-1.198-0.636-2.497-0.325-3.744-0.325C177.163,229.305,126.184,229.307,75.205,229.307z
  M150.446,313.388c17.521-11.574,35.042-23.148,53.011-35.018c5.31,24.208,10.52,47.964,15.748,71.799
 c-50.462-3.844-101.011-48.047-106.656-107.186c24.109,5.292,47.89,10.511,72.185,15.844
 c-11.752,18.288-23.255,36.187-34.757,54.087C150.134,313.072,150.29,313.23,150.446,313.388z M159.335,301.804
 c8.69-13.535,17.862-27.819,27.148-42.283c4.105,1.182,8.069,2.323,11.847,3.411C185.111,276.109,171.94,289.239,159.335,301.804z
  M133.835,330.601c-29.246-28.459-41.3-66.425-41.624-91.863c1.943,0.023,3.762,0.571,5.826,1.146
 c3.008,33.024,15.766,61.703,39.248,85.294c23.409,23.519,51.894,36.473,84.782,39.638c1.031,2.397,1.274,3.552,1.277,6.314
 C188.619,368.267,158.769,354.864,133.835,330.601z M232.664,390.964c0,0.524-0.094,1.047-0.561,1.617
 c-9.622-45.424-19.243-90.849-28.907-136.471c3.029-2.763,6.015-5.506,9.023-8.227c5.831-5.275,11.685-10.527,17.501-15.819
 c0.776-0.706,1.778-1.23,2.187-2.291c1.162,0.508,0.747,1.565,0.747,2.405C232.672,285.106,232.671,338.035,232.664,390.964z
  M326.131,122.696c29.266,25.597,45.29,58.025,48.646,96.792c-2.425,0.259-3.141,0.14-6.337-1.007
 c-3.056-32.989-16.14-61.536-39.774-84.952c-23.56-23.342-52.16-36.117-85.148-39.023c-0.804-2-1.177-3.858-1.373-6.246
 C274.11,90.495,302.192,101.758,326.131,122.696z M316.28,146.614c-0.142-0.184-0.284-0.368-0.426-0.552
 c-17.656,11.246-35.312,22.491-53.432,34.032c-5.346-23.914-10.59-47.37-15.947-71.334c27.217,3.187,50.505,13.817,70.09,32.277
 c21.371,20.143,33.82,44.903,37.702,74.667c-24.204-5.22-47.901-10.331-72.178-15.567
 C293.708,181.948,304.994,164.281,316.28,146.614z M279.269,199.894c-4.497-1.218-8.742-2.367-12.764-3.456
 c13.301-13.293,26.523-26.508,39.169-39.147C297.264,170.86,288.362,185.223,279.269,199.894z M233.324,66.818
 c0.111-0.023,0.222-0.046,0.333-0.069c9.894,45.353,19.787,90.705,29.704,136.164c-9.981,8.64-19.82,17.156-30.038,26
 C233.324,174.571,233.324,120.695,233.324,66.818z M344.815,317.096c-26.078,32.77-60.332,50.502-102.474,54.186
 c0.137-1.296,0.159-2.03,0.303-2.739c0.237-1.163,0.553-2.31,0.876-3.628c33.296-3.014,62.022-15.889,85.617-39.518
 c23.531-23.566,36.454-52.18,39.368-85.19c0.953-0.831,1.992-0.902,2.971-1.115c0.895-0.195,1.804-0.328,3.296-0.592
 C372.856,267.994,363.007,294.236,344.815,317.096z M262.428,278.164c18.04,11.873,35.682,23.483,53.323,35.093
 c0.178-0.15,0.356-0.299,0.534-0.449c-11.346-17.856-22.692-35.711-34.379-54.103c24.328-5.317,48.108-10.515,72.435-15.832
 c-3.507,28.869-15.194,53.129-35.348,73.162c-19.879,19.76-43.926,31.11-72.436,34.674
 C251.895,326.312,257.11,302.471,262.428,278.164z M262.383,275.129c1.098-4.556,2.09-8.668,3.06-12.694
 c13.418,13.705,26.798,27.372,37.625,38.432C291.714,293.684,277.121,284.453,262.383,275.129z M262.468,254.569
 c-9.438-8.191-18.899-16.402-28.524-24.755c2.887-0.751,158.569-0.725,160.993-0.071
 C392.556,230.445,265.89,254.215,262.468,254.569z"/> <path d="M45.572,212.585c-1.732,7.026-3.442,13.962-5.152,20.898c-0.221-0.004-0.442-0.009-0.664-0.013
 c-1.655-6.954-3.31-13.908-4.963-20.859c-1.933-0.276-3.553-0.385-5.393,0.074c-1.705,6.962-3.414,13.935-5.122,20.908
 c-0.174,0.009-0.348,0.019-0.522,0.028c-1.82-7.018-3.64-14.036-5.514-21.259c-1.715,0.182-3.325-0.38-5.075,0.446
 c2.199,8.72,4.358,17.281,6.523,25.866c2.632,0.247,4.985,0.387,7.493-0.111c1.495-5.474,2.973-10.882,4.45-16.289
 c0.19,0.019,0.381,0.038,0.571,0.056c1.471,5.485,2.942,10.97,4.405,16.425c2.552,0.126,4.744,0.304,7.143-0.137
 c2.148-8.637,4.295-17.267,6.454-25.945C48.486,212.221,47.22,212.319,45.572,212.585z"/> <path d="M225.389,37.909c0-6.944,0-13.947,0-20.951c0.17-0.021,0.34-0.042,0.51-0.063c4.007,7.146,8.014,14.293,12.051,21.492
 c1.949-0.144,3.681,0.3,5.439-0.343c0-9.247,0-18.414,0-27.62c-1.678-0.164-3.116-0.448-4.762,0.306c0,6.137,0,12.264,0,18.392
 c-0.188,0.055-0.375,0.109-0.563,0.164c-3.632-6.352-7.264-12.703-10.843-18.961c-2.329,0-4.352,0-6.643,0
 c0,9.304,0,18.534,0,27.955C222.177,38.323,223.611,38.624,225.389,37.909z"/> <path d="M241.509,436.792c-2.469-1.847-5.379-2.839-8.151-4.097c-1.681-0.763-3.404-1.475-4.706-2.856
 c-1.84-1.951-1.374-4.333,1.034-5.393c4.07-1.792,7.919-0.299,9.604,3.785c1.507,0.173,3.042,0.397,4.633-0.202
 c0.316-2.636-1.036-4.356-3.176-5.287c-5.065-2.204-10.312-2.275-15.339-0.038c-4.13,1.838-4.466,6.377-0.924,9.184
 c2.199,1.742,4.836,2.675,7.353,3.826c1.929,0.882,3.965,1.603,5.529,3.107c1.842,1.772,1.585,4.145-0.512,5.344
 c-4.334,2.478-9.149,0.46-10.182-4.417c-1.398,0-2.836,0-4.467,0c-0.435,3.42,0.401,5.336,3.196,6.465
 c4.993,2.016,10.128,2.128,15.1,0.004C244.977,444.304,245.393,439.699,241.509,436.792z"/> <path d="M444.156,236.697c-4.439,0-9.19,0-14.116,0c-0.061-3.467-0.344-6.549,0.238-9.914c3.97,0,7.721,0,11.331,0
 c0.447-1.02,0.5-1.717-0.139-2.77c-3.606,0-7.429,0-11.367,0c0-3.289,0-6.207,0-9.604c5.067,0,9.747,0,14.216,0
 c0.653-0.995,0.639-1.684,0.186-2.526c-6.534,0-13.131,0-19.799,0c0,9.297,0,18.386,0,27.607c6.751,0,13.277,0,19.873,0
 C444.827,238.59,445.149,237.825,444.156,236.697z"/>`;

function compassSvg(w, h, px, py, skipBacker) {
  if (compassCorner === 'off') return '';
  const sz = Math.min(w, h) * 0.12 * compassSzMult;
  const scale = sz / 458;
  const margin = sz * 0.3;
  const hW = w/2, hH = h/2;
  let cx, cy;
  if (compassCorner === 'tl') { cx = px - hW + margin + sz/2; cy = py - hH + margin + sz/2; }
  else if (compassCorner === 'tr') { cx = px + hW - margin - sz/2; cy = py - hH + margin + sz/2; }
  else if (compassCorner === 'bl') { cx = px - hW + margin + sz/2; cy = py + hH - margin - sz/2; }
  else { cx = px + hW - margin - sz/2; cy = py + hH - margin - sz/2; }
  cx += compassOffX; cy += compassOffY;
  const tx = cx - sz/2, ty = cy - sz/2;
  const backer = skipBacker ? '' : `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="${(sz*0.56).toFixed(1)}" fill="#ded8c5"/>`;
  // Rotate the compass graphic by -mapBearing so the north arrow stays
  // accurate when the map is rotated. The rotation is around the compass
  // center (cx, cy), applied as an outer transform wrapping the existing
  // translate+scale.
  const rot = mapBearing ? ` rotate(${(-mapBearing).toFixed(1)},${cx.toFixed(1)},${cy.toFixed(1)})` : '';
  return backer + `<g transform="${rot} translate(${tx.toFixed(1)},${ty.toFixed(1)}) scale(${scale.toFixed(6)})" fill="#594a42">${COMPASS_SVG}</g>`;
}

// Title + subtitle
function titleSvg(w, h, px, py) {
  const title = document.getElementById('title-input').value.trim();
  const subtitle = document.getElementById('subtitle-input').value.trim();
  if (!title && !subtitle) return '';
  const hW = w/2, hH = h/2;
  const titleSz = Math.min(w, h) * 0.045 * titleSzMult;
  const subSz = Math.min(w, h) * 0.025 * subSzMult;
  const offsetX = w * titleDx / 100, offsetY = h * titleDy / 100;
  const totalTextH = (title ? titleSz * lineGap : 0) + (subtitle ? subSz * lineGap : 0);
  let baseY = py + hH - totalTextH - titleSz * 0.4 + offsetY;
  let baseX = px - hW + titleSz * 0.5 + offsetX;
  let subBaseX = baseX + w * subDx / 100;
  let out = '';
  const strokeW = (titleSz * 0.35).toFixed(1);
  const strokeAttr = ' stroke="#ded8c5" stroke-width="'+strokeW+'" stroke-linejoin="round" paint-order="stroke"';

  // Rect backer mode - measure actual text widths
  if (titleBackerStyle === 'rect') {
    const pad = titleSz * 0.3 * rectPadMult;
    const titleW = title ? measureTextWidth(title, titleFont, titleSz, '800') : 0;
    const subW = subtitle ? measureTextWidth(subtitle, 'Inter,Helvetica,sans-serif', subSz, '600') : 0;
    const hPadExtra = titleSz * 0.15; // extra horizontal padding
    const leftEdge = Math.min(baseX, subBaseX) - pad - hPadExtra;
    const rightEdge = Math.max(baseX + titleW, subBaseX + subW) + pad + hPadExtra;
    out += '<rect x="'+leftEdge.toFixed(1)+'" y="'+(baseY - pad).toFixed(1)+'" width="'+(rightEdge - leftEdge).toFixed(1)+'" height="'+(totalTextH + pad * 2).toFixed(1)+'" rx="'+(titleSz * 0.15).toFixed(1)+'" fill="#ded8c5"/>\n';
  }

  // Unified offset: render ALL text with stroke first (background layer)
  if (titleOffsetOn) {
    if (title) {
      const y = baseY + titleSz * 0.9;
      out += '<text x="'+baseX.toFixed(1)+'" y="'+y.toFixed(1)+'" font-family="'+titleFont+'" font-size="'+titleSz.toFixed(1)+'" font-weight="800" fill="#ded8c5" letter-spacing="0"'+strokeAttr+'>'+title.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')+'</text>\n';
    }
    if (subtitle) {
      const y = baseY + (title ? titleSz * lineGap : 0) + subSz * 0.9;
      out += '<text x="'+subBaseX.toFixed(1)+'" y="'+y.toFixed(1)+'" font-family="Inter,Helvetica,sans-serif" font-size="'+subSz.toFixed(1)+'" font-weight="600" fill="#ded8c5" letter-spacing="0"'+strokeAttr+'>'+subtitle.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')+'</text>\n';
    }
  }

  // Foreground text (on top of offset)
  if (title) {
    const y = baseY + titleSz * 0.9;
    out += '<text x="'+baseX.toFixed(1)+'" y="'+y.toFixed(1)+'" font-family="'+titleFont+'" font-size="'+titleSz.toFixed(1)+'" font-weight="800" fill="#594a42" letter-spacing="0">'+title.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')+'</text>\n';
  }
  if (subtitle) {
    const y = baseY + (title ? titleSz * lineGap : 0) + subSz * 0.9;
    out += '<text x="'+subBaseX.toFixed(1)+'" y="'+y.toFixed(1)+'" font-family="Inter,Helvetica,sans-serif" font-size="'+subSz.toFixed(1)+'" font-weight="600" fill="#594a42" letter-spacing="0">'+subtitle.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')+'</text>\n';
  }
  return out;
}

function labelSvg(layers, skipBackers) {
  if (!showLabel || !layers.visibleLabels || !layers.visibleLabels.length) return '';
  const mapSize = Math.min(layers.halfW * 2, layers.halfH * 2);
  const placed = [];
  const filtered = [];
  // Compass exclusion zone
  const cb = layers.compassBacker;
  const cbCx = cb && cb._cx, cbCy = cb && cb._cy, cbR = cb && cb._r;
  for (const l of layers.visibleLabels) {
    const sz = (l.rank <= 1 ? mapSize * 0.025 : l.rank <= 2 ? mapSize * 0.018 : l.rank <= 3 ? mapSize * 0.013 : mapSize * 0.01) * labelSzMult;
    if (sz < 1.5) continue;
    // Skip labels that overlap the compass backer
    if (cbR && Math.hypot(l.x - cbCx, l.y - cbCy) < cbR * 1.1) continue;
    const minDist = sz * l.name.length * 0.45;
    const tooClose = placed.some(p => Math.hypot(p.x - l.x, p.y - l.y) < Math.max(minDist, p.minDist));
    if (!tooClose) { placed.push({ x: l.x, y: l.y, minDist }); filtered.push({ ...l, sz }); }
  }
  let out = '\n';
  for (const l of filtered) {
    const esc = l.name.replace(/&/g, '&amp;').replace(/</g, '&lt;');
    if (!skipBackers) {
      const tw = l.sz * l.name.length * 0.52, pad = l.sz * 0.35, rh = l.sz * 1.4;
      out += '    <rect x="' + (l.x-tw/2-pad).toFixed(1) + '" y="' + (l.y-rh/2).toFixed(1) + '" width="' + (tw+pad*2).toFixed(1) + '" height="' + rh.toFixed(1) + '" rx="' + (l.sz*0.2).toFixed(1) + '" fill="#ded8c5"/>\n';
    }
    out += '    <text x="' + l.x.toFixed(1) + '" y="' + l.y.toFixed(1) + '" font-family="Inter,Helvetica,Arial,sans-serif" font-size="' + l.sz.toFixed(2) + '" font-weight="' + (l.rank<=2?'700':'600') + '" fill="#594a42" text-anchor="middle" dominant-baseline="central">' + esc + '</text>\n';
  }
  return out;
}

// Preview rendering for curved road name labels. Each letter is its own
// <text> element, positioned and rotated at its own spot on the road's
// polyline — the exact same arc-length walk the export's
// textToPolygonsAlongPath uses, just outputting SVG <text> instead of
// opentype.js polygon rings. This avoids <textPath>, which is fragile
// across renderers and when embedded via innerHTML.
function roadLabelSvg(layers) {
  if (!showRoadLabels || !layers.roadLabels || !layers.roadLabels.length) return '';
  // Compute font size LIVE from the current multiplier, not from the
  // baked-in rl.sz in cached layers. Font-size buttons take effect
  // instantly via overlay-only rerender without rebuilding layers.
  const mapSz = Math.min(layers.halfW * 2, layers.halfH * 2);
  const liveSz = mapSz * 0.015 * roadLabelSzMult;
  let out = '\n';
  for (const rl of layers.roadLabels) {
    // Skip tertiary road labels when showing main roads only
    if (roadMode === 'main' && !rl.isMajor) continue;
    if (roadMode === 'off') continue;
    const pts = rl.pts;
    const sz = liveSz;
    const cum = [0];
    for (let i = 1; i < pts.length; i++)
      cum.push(cum[i - 1] + Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y));
    const total = cum[cum.length - 1];
    if (total < 1) continue;

    const chars = Array.from(rl.name);
    const spaceW = measureTextWidth('x x', 'Inter,Helvetica,sans-serif', sz, '700')
                 - measureTextWidth('xx', 'Inter,Helvetica,sans-serif', sz, '700');
    const tracking = sz * 0.02;
    const advances = chars.map(c => (c === ' ' ? Math.max(spaceW, sz * 0.25) : measureTextWidth(c, 'Inter,Helvetica,sans-serif', sz, '700')) + tracking);
    const textWidth = advances.reduce((a, b) => a + b, 0);
    // No fit check here — the quality gate below catches truly bad overflow
    // (stacked letters). Removing this prevents labels from vanishing one by
    // one as the user increases font size by a click or two.

    const labelStart = rl.anchorS - textWidth / 2;
    const labelEnd = rl.anchorS + textWidth / 2;
    function posAt(s) {
      s = Math.max(0, Math.min(total, s));
      let i = 0;
      while (i < cum.length - 2 && cum[i + 1] < s) i++;
      const segLen = (cum[i + 1] - cum[i]) || 1e-6;
      const t = (s - cum[i]) / segLen;
      const p0 = pts[i], p1 = pts[i + 1];
      return { x: p0.x + (p1.x - p0.x) * t, y: p0.y + (p1.y - p0.y) * t };
    }
    const pStart = posAt(labelStart), pEnd = posAt(labelEnd);
    const avgAngle = Math.atan2(pEnd.y - pStart.y, pEnd.x - pStart.x);
    // Refs: centered on the road (no offset). Names: offset to the side.
    const offX = rl.isRef ? 0 : -Math.sin(avgAngle) * sz * 1.1 * (roadLabelFlip ? -1 : 1);
    const offY = rl.isRef ? 0 :  Math.cos(avgAngle) * sz * 1.1 * (roadLabelFlip ? -1 : 1);

    function sampleAt(s) {
      // Extrapolate past chain endpoints instead of clamping. When the text
      // is wider than the chain at a larger font size, letters at the edges
      // continue along the road's direction rather than stacking at the
      // endpoint (which the quality gate would reject as "jumbled").
      if (s < 0) {
        const p0 = pts[0], p1 = pts[Math.min(1, pts.length - 1)];
        const angle = Math.atan2(p1.y - p0.y, p1.x - p0.x);
        return { x: p0.x + Math.cos(angle) * s + offX, y: p0.y + Math.sin(angle) * s + offY, angle: angle * 180 / Math.PI };
      }
      if (s > total) {
        const p0 = pts[pts.length - 2] || pts[0], p1 = pts[pts.length - 1];
        const angle = Math.atan2(p1.y - p0.y, p1.x - p0.x);
        const excess = s - total;
        return { x: p1.x + Math.cos(angle) * excess + offX, y: p1.y + Math.sin(angle) * excess + offY, angle: angle * 180 / Math.PI };
      }
      let i = 0;
      while (i < cum.length - 2 && cum[i + 1] < s) i++;
      const segLen = (cum[i + 1] - cum[i]) || 1e-6;
      const t = (s - cum[i]) / segLen;
      const p0 = pts[i], p1 = pts[i + 1];
      const halfSz = sz * 0.8;
      const tA = posAt(Math.max(0, s - halfSz));
      const tB = posAt(Math.min(total, s + halfSz));
      const angleRad = Math.atan2(tB.y - tA.y, tB.x - tA.x);
      return {
        x: p0.x + (p1.x - p0.x) * t + offX,
        y: p0.y + (p1.y - p0.y) * t + offY,
        angle: angleRad * 180 / Math.PI,
      };
    }

    // Two-pass: compute all positions first, then quality-check before emitting.
    // Bad geometry (loops, kinks from smoothing artifacts) causes letters to
    // bunch up or overlap — "jumbled" text that's impossible to laser-cut.
    let s = labelStart;
    const letterPositions = [];
    for (let ci = 0; ci < chars.length; ci++) {
      const adv = advances[ci];
      if (chars[ci] !== ' ') {
        const mid = sampleAt(s + adv / 2);
        letterPositions.push({ char: chars[ci], x: mid.x, y: mid.y, angle: mid.angle, idx: ci });
      }
      s += adv;
    }

    // Quality gate: skip this label if the geometry produces overlapping or
    // wildly misaligned letters. Use the EXPECTED advance as the baseline —
    // if consecutive letters are closer than 25% of what they should be,
    // the geometry has a loop or kink. Using the advance ratio (not an
    // absolute distance) prevents small font sizes from being rejected
    // just because letters are naturally close together.
    let bad = false;
    let advIdx = 0;
    for (let li = 0; li < chars.length; li++) {
      if (chars[li] === ' ') { advIdx++; continue; }
      // Find next visible character
      let nextVis = -1;
      for (let ni = li + 1; ni < chars.length; ni++) {
        if (chars[ni] !== ' ') { nextVis = ni; break; }
      }
      if (nextVis < 0) break;
      const posA = letterPositions.find(lp => lp.idx === li);
      const posB = letterPositions.find(lp => lp.idx === nextVis);
      if (posA && posB) {
        const dist = Math.hypot(posB.x - posA.x, posB.y - posA.y);
        // Sum advances between these two characters
        let expectedDist = 0;
        for (let ai = li; ai < nextVis; ai++) expectedDist += advances[ai];
        if (expectedDist > 0 && dist < expectedDist * 0.2) { bad = true; break; }
        const angleDiff = Math.abs(posB.angle - posA.angle);
        if (angleDiff > 50 && angleDiff < 310) { bad = true; break; }
      }
    }
    if (bad) continue;

    // Refs: draw a land-colored backer rect behind the text to visually
    // knock out the road. Rotated to match the road's average angle.
    if (rl.isRef && letterPositions.length) {
      const cx = (posAt(labelStart).x + posAt(labelEnd).x) / 2;
      const cy = (posAt(labelStart).y + posAt(labelEnd).y) / 2;
      const bw = textWidth + sz * 0.6;
      const bh = sz * 1.4;
      const deg = avgAngle * 180 / Math.PI;
      out += '    <rect x="' + (-bw/2).toFixed(2) + '" y="' + (-bh/2).toFixed(2) + '" width="' + bw.toFixed(2) + '" height="' + bh.toFixed(2) + '" rx="' + (sz * 0.15).toFixed(2) + '" fill="#ded8c5" transform="translate(' + cx.toFixed(2) + ' ' + cy.toFixed(2) + ') rotate(' + deg.toFixed(1) + ')"/>\n';
    }

    for (const lp of letterPositions) {
      const esc = lp.char.replace(/&/g, '&amp;').replace(/</g, '&lt;');
      out += '    <text x="' + lp.x.toFixed(2) + '" y="' + lp.y.toFixed(2) + '" transform="rotate(' + lp.angle.toFixed(1) + ' ' + lp.x.toFixed(2) + ' ' + lp.y.toFixed(2) + ')" font-family="Inter,Helvetica,Arial,sans-serif" font-size="' + sz.toFixed(2) + '" font-weight="700" fill="#594a42" text-anchor="middle" dominant-baseline="central">' + esc + '</text>\n';
    }
  }
  return out;
}

function svgWrap(w, h, px, py, inner, outW, outH) {
  const hW = w/2, hH = h/2;
  const ow = outW || w, oh = outH || h;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${px-hW} ${py-hH} ${w} ${h}" width="${ow}mm" height="${oh}mm">\n${inner}\n</svg>`;
}

// Palette: 6 colours deepest → lightest.
const PALETTE = ['#00365b', '#005079', '#006f93', '#0088a5', '#2da4c2', '#76cbea'];

// Assigns colors to whichever depth bands are currently checked, spread
// evenly across the full dark-to-light palette regardless of which specific
// bands are active (e.g. only bands 3 and 5 checked still spans the full
// range the same way 2 contiguous bands would).
function getBandColors() {
  const order = [5, 4, 3, 2, 1]; // deepest to shallowest
  const positions = {};
  let count = 0;
  for (const k of order) {
    if (enabledBands[k]) { count++; positions[k] = count; }
  }
  const nWater = count + 1; // base + active bands
  const colorAt = (pos) => PALETTE[Math.min(5, Math.floor(pos * 6 / nWater))];
  return {
    baseColor: colorAt(0),
    band1Color: enabledBands[1] ? colorAt(positions[1]) : '',
    band2Color: enabledBands[2] ? colorAt(positions[2]) : '',
    band3Color: enabledBands[3] ? colorAt(positions[3]) : '',
    band4Color: enabledBands[4] ? colorAt(positions[4]) : '',
    band5Color: enabledBands[5] ? colorAt(positions[5]) : ''
  };
}

function buildFullSvg(layers, w, h, px, py) {
  const hW = w/2, hH = h/2;

  // Cache expensive polygon → path-string conversions on the layers object.
  const ck = w + ',' + h + ',' + px + ',' + py;
  if (!layers._svgCache || layers._svgCacheKey !== ck) {
    layers._svgCacheKey = ck;
    const c = {};
    c.land = polygonsToPathD(layers.land);
    c.band5 = layers.band5Holes && layers.band5Holes.length ? bandPathD(layers.band5Holes, w, h, px, py) : '';
    c.band4 = layers.band4Holes && layers.band4Holes.length ? bandPathD(layers.band4Holes, w, h, px, py) : '';
    c.band3 = layers.outerHoles.length ? bandPathD(layers.outerHoles, w, h, px, py) : '';
    c.band2 = layers.midHoles.length ? bandPathD(layers.midHoles, w, h, px, py) : '';
    c.band1 = layers.shallowHoles.length ? bandPathD(layers.shallowHoles, w, h, px, py) : '';
    c.majorD = '';
    if (layers.majorLines && layers.majorLines.length) {
      for (const ln of layers.majorLines) {
        if (ln.length < 2) continue;
        c.majorD += ln.map((p, i) => (i === 0 ? 'M' : 'L') + ' ' + p.x.toFixed(2) + ',' + p.y.toFixed(2)).join(' ') + ' ';
      }
    }
    c.minorD = '';
    if (layers.minorLines && layers.minorLines.length) {
      for (const ln of layers.minorLines) {
        if (ln.length < 2) continue;
        c.minorD += ln.map((p, i) => (i === 0 ? 'M' : 'L') + ' ' + p.x.toFixed(2) + ',' + p.y.toFixed(2)).join(' ') + ' ';
      }
    }
    layers._svgCache = c;
  }
  const c = layers._svgCache;

  const { baseColor, band1Color, band2Color, band3Color, band4Color, band5Color } = getBandColors();

  // Base layer: the heavy path data (bands, land, roads). Persists in DOM
  // across light rerenders so the browser doesn't have to re-parse megabytes
  // of path data when you just move the compass.
  const baseSvg = `    <rect data-band="base" x="${px-hW}" y="${py-hH}" width="${w}" height="${h}" fill="${baseColor}"/>
${c.band5 ? '    <path data-band="5" d="' + c.band5 + '" fill="' + (enabledBands[5] ? band5Color : 'none') + '" fill-rule="evenodd"' + (enabledBands[5] ? '' : ' style="display:none"') + '/>' : ''}
${c.band4 ? '    <path data-band="4" d="' + c.band4 + '" fill="' + (enabledBands[4] ? band4Color : 'none') + '" fill-rule="evenodd"' + (enabledBands[4] ? '' : ' style="display:none"') + '/>' : ''}
${c.band3 ? '    <path data-band="3" d="' + c.band3 + '" fill="' + (enabledBands[3] ? band3Color : 'none') + '" fill-rule="evenodd"' + (enabledBands[3] ? '' : ' style="display:none"') + '/>' : ''}
${c.band2 ? '    <path data-band="2" d="' + c.band2 + '" fill="' + (enabledBands[2] ? band2Color : 'none') + '" fill-rule="evenodd"' + (enabledBands[2] ? '' : ' style="display:none"') + '/>' : ''}
${c.band1 ? '    <path data-band="1" d="' + c.band1 + '" fill="' + (enabledBands[1] ? band1Color : 'none') + '" fill-rule="evenodd"' + (enabledBands[1] ? '' : ' style="display:none"') + '/>' : ''}
    <path d="${c.land}" fill="#ded8c5" fill-rule="evenodd"/>`;

  // Overlay: roads + labels + compass + title + frame. Roads are here (not
  // in the base layer) so thickness and mode changes take effect instantly.
  const overlaySvg = buildOverlaySvg(layers, w, h, px, py, c);

  return svgWrap(w, h, px, py,
    '<g class="base-layer">' + baseSvg + '</g>\n<g class="overlay-layer">' + overlaySvg + '</g>');
}

function buildOverlaySvg(layers, w, h, px, py, pathCache) {
  // Roads: use cached path strings but apply current thickness/mode
  let roads = '';
  const c = pathCache || (layers._svgCache || {});
  if (roadMode !== 'off') {
    const majorW = 0.5 * roadThickMult;
    const minorW = 0.22 * roadThickMult;
    if (c.majorD) roads += '\n    <path d="' + c.majorD + '" fill="none" stroke="#594a42" stroke-width="' + majorW.toFixed(2) + '" stroke-linecap="round" stroke-linejoin="round"/>';
    if (roadMode === 'all' && c.minorD) roads += '\n    <path d="' + c.minorD + '" fill="none" stroke="#594a42" stroke-width="' + minorW.toFixed(2) + '" stroke-linecap="round" stroke-linejoin="round"/>';
  }
  const frameT = Math.min(w, h) * frameThickness;
  return roads + roadLabelSvg(layers) + labelSvg(layers) + '\n    ' +
    frameSvg(w, h, px, py, frameT, "#ded8c5") + '\n    ' +
    compassSvg(w, h, px, py) + '\n    ' +
    titleSvg(w, h, px, py);
}

function buildLayeredExportSvg(layers, w, h, px, py, outW, outH, otTitleFont, otInterFont) {
  const gap = Math.max(14, Math.round(Math.max(w, h) * 0.06));
  const fontSize2 = Math.max(3.5, +(Math.max(w, h) * 0.016).toFixed(2));
  const labelGap = fontSize2 * 2.2;
  const hW = w/2, hH = h/2;
  const pad = fontSize2 * 1.5;
  let exportRoads = layers.allRoadPolys;
  // Roads will be knocked out under title offset and compass backer in the final panel build
  const roads = ''; // placeholder, actual roads rendered per-panel below

  // Build land layer with ALL merges: frame + compass backer + label backers + title offset
  let exportLand = layers.land.slice();

  // Merge frame
  if (layers.ft > 0) {
    const frameShape = differencePolygons(
      [[{x:px-hW,y:py-hH},{x:px+hW,y:py-hH},{x:px+hW,y:py+hH},{x:px-hW,y:py+hH}]],
      [[{x:px-hW+layers.ft,y:py-hH+layers.ft},{x:px+hW-layers.ft,y:py-hH+layers.ft},{x:px+hW-layers.ft,y:py+hH-layers.ft},{x:px-hW+layers.ft,y:py+hH-layers.ft}]]
    );
    exportLand = unionPolygons([...exportLand, ...frameShape]);
  }

  // Compass backer already merged into land in buildLayers

  // Label backers already merged into land in buildLayers

  let titleKnockout = [];
  // Title offset: expand text outlines and merge into land
  const titleText = document.getElementById('title-input').value.trim();
  const subtitleText = document.getElementById('subtitle-input').value.trim();
  if (titleOffsetOn && (titleText || subtitleText)) {
    const titleSz = Math.min(w, h) * 0.045 * titleSzMult;
    const subSz2 = Math.min(w, h) * 0.025 * subSzMult;
    const offsetX = w * titleDx / 100, offsetY = h * titleDy / 100;
    const totalTextH = (titleText ? titleSz * lineGap : 0) + (subtitleText ? subSz2 * lineGap : 0);
    const baseY = py + hH - totalTextH - titleSz * 0.4 + offsetY;
    const baseX = px - hW + titleSz * 0.5 + offsetX;
    const subBaseX2 = baseX + w * subDx / 100;
    const expandDist = titleSz * 0.18;

    // titleKnockout already declared above
    if (titleText && otTitleFont) {
      const y = baseY + titleSz * 0.9;
      const textPolys = textToPolygons(titleText, otTitleFont, titleSz, baseX, y);
      if (textPolys.length) {
        const expanded = offsetPolygons(textPolys, expandDist);
        if (expanded.length) {
          exportLand = unionPolygons([...exportLand, ...expanded]);
          titleKnockout.push(...expanded);
        }
      }
    }
    if (subtitleText && otInterFont) {
      const y = baseY + (titleText ? titleSz * lineGap : 0) + subSz2 * 0.9;
      const textPolys = textToPolygons(subtitleText, otInterFont, subSz2, subBaseX2, y);
      if (textPolys.length) {
        const expanded = offsetPolygons(textPolys, expandDist);
        if (expanded.length) {
          exportLand = unionPolygons([...exportLand, ...expanded]);
          titleKnockout.push(...expanded);
        }
      }
    }
  }

  // Rectangle backer style: merge a padded rect behind the title/subtitle into
  // the land layer (mirrors the light rect the preview draws in titleSvg()).
  // This was previously only drawn in the preview and never made it into the
  // exported/cut file.
  if (titleBackerStyle === 'rect' && (titleText || subtitleText)) {
    const titleSz = Math.min(w, h) * 0.045 * titleSzMult;
    const subSz2 = Math.min(w, h) * 0.025 * subSzMult;
    const offsetX = w * titleDx / 100, offsetY = h * titleDy / 100;
    const totalTextH = (titleText ? titleSz * lineGap : 0) + (subtitleText ? subSz2 * lineGap : 0);
    const baseY = py + hH - totalTextH - titleSz * 0.4 + offsetY;
    const baseX = px - hW + titleSz * 0.5 + offsetX;
    const subBaseX2 = baseX + w * subDx / 100;
    const pad = titleSz * 0.3 * rectPadMult;
    const hPadExtra = titleSz * 0.15;
    const titleW = (titleText && otTitleFont) ? otTitleFont.getAdvanceWidth(titleText, titleSz) : 0;
    const subW = (subtitleText && otInterFont) ? otInterFont.getAdvanceWidth(subtitleText, subSz2) : 0;
    const leftEdge = Math.min(baseX, subBaseX2) - pad - hPadExtra;
    const rightEdge = Math.max(baseX + titleW, subBaseX2 + subW) + pad + hPadExtra;
    const topEdge = baseY - pad;
    const bottomEdge = baseY + totalTextH + pad;
    const rectPoly = [[
      { x: leftEdge, y: topEdge },
      { x: rightEdge, y: topEdge },
      { x: rightEdge, y: bottomEdge },
      { x: leftEdge, y: bottomEdge }
    ]];
    const cleanedRect = cleanupPolygons(rectPoly);
    if (cleanedRect.length) {
      exportLand = unionPolygons([...exportLand, ...cleanedRect]);
      titleKnockout.push(...cleanedRect);
    }
  }

  // Convert title/subtitle text to paths (instead of <text> elements)
  let titlePaths = '';
  if (titleText || subtitleText) {
    const titleSz = Math.min(w, h) * 0.045 * titleSzMult;
    const subSz2 = Math.min(w, h) * 0.025 * subSzMult;
    const offsetX = w * titleDx / 100, offsetY = h * titleDy / 100;
    const totalTextH = (titleText ? titleSz * lineGap : 0) + (subtitleText ? subSz2 * lineGap : 0);
    const baseY = py + hH - totalTextH - titleSz * 0.4 + offsetY;
    const baseX = px - hW + titleSz * 0.5 + offsetX;
    const subBaseX2 = baseX + w * subDx / 100;

    if (titleText && otTitleFont) {
      const y = baseY + titleSz * 0.9;
      const polys = textToPolygons(titleText, otTitleFont, titleSz, baseX, y);
      if (polys.length) titlePaths += '<path d="' + polygonsToPathD(polys) + '" fill="#594a42" fill-rule="evenodd"/>';
    }
    if (subtitleText && otInterFont) {
      const y = baseY + (titleText ? titleSz * lineGap : 0) + subSz2 * 0.9;
      const polys = textToPolygons(subtitleText, otInterFont, subSz2, subBaseX2, y);
      if (polys.length) titlePaths += '<path d="' + polygonsToPathD(polys) + '" fill="#594a42" fill-rule="evenodd"/>';
    }
  }

  // Convert place labels to paths using opentype.js
  let labelPaths = '';
  const exportLabelBackerRects = []; // for knocking out road labels
  if (showLabel && layers.visibleLabels && otInterFont) {
    const mapSize = Math.min(layers.halfW * 2, layers.halfH * 2);
    const placed = [];
    const cb = layers.compassBacker;
    const cbCx = cb && cb._cx, cbCy = cb && cb._cy, cbR = cb && cb._r;
    for (const l of layers.visibleLabels) {
      const sz = (l.rank <= 1 ? mapSize * 0.025 : l.rank <= 2 ? mapSize * 0.018 : l.rank <= 3 ? mapSize * 0.013 : mapSize * 0.01) * labelSzMult;
      if (sz < 1.5) continue;
      if (cbR && Math.hypot(l.x - cbCx, l.y - cbCy) < cbR * 1.1) continue;
      const minDist = sz * l.name.length * 0.45;
      const tooClose = placed.some(p => Math.hypot(p.x - l.x, p.y - l.y) < Math.max(minDist, p.minDist));
      if (tooClose) continue;
      placed.push({ x: l.x, y: l.y, minDist });
      // Measure text width for centering
      const textWidth = otInterFont.getAdvanceWidth(l.name, sz);
      const tx = l.x - textWidth / 2;
      const ty = l.y + sz * 0.35;
      const polys = textToPolygons(l.name, otInterFont, sz, tx, ty);
      if (polys.length) {
        // Bold: expand glyph outlines to match preview font-weight 700
        let bold;
        try { bold = offsetPolygons(polys, sz * 0.025); } catch(e) { bold = polys; }
        if (!bold || !bold.length) bold = polys;
        labelPaths += '<path d="' + polygonsToPathD(bold) + '" fill="#594a42" fill-rule="evenodd"/>';
      }
      // Collect backer rectangle for knocking out road labels
      const tw = sz * l.name.length * 0.52, pd = sz * 0.35, rh = sz * 1.4;
      const rx = l.x - tw/2 - pd, ry = l.y - rh/2;
      exportLabelBackerRects.push([{x:rx,y:ry},{x:rx+tw+pd*2,y:ry},{x:rx+tw+pd*2,y:ry+rh},{x:rx,y:ry+rh}]);
    }
  }

  // Road labels: render, bold, clip to viewport, and knock out from roads.
  // Road label polygons that overlap place-label backers or the compass backer
  // are subtracted so the land-colored backer shows through cleanly — matching
  // the preview where SVG z-ordering handles this automatically.
  let roadLabelPaths = '';
  const roadLabelKnockoutPolys = [];
  let allRoadLabelPolys = []; // collect for backer subtraction
  const exportClipRect = [[{x:px-hW,y:py-hH},{x:px+hW,y:py-hH},{x:px+hW,y:py+hH},{x:px-hW,y:py+hH}]];
  if (showRoadLabels && layers.roadLabels && layers.roadLabels.length && otInterFont) {
    for (const rl of layers.roadLabels) {
      if (roadMode === 'main' && !rl.isMajor) continue;
      if (roadMode === 'off') continue;
      const exportOffset = rl.isRef ? 0 : rl.sz * 1.1 * (roadLabelFlip ? -1 : 1);
      const polys = textToPolygonsAlongPath(rl.name, otInterFont, rl.sz, rl.pts, exportOffset, rl.anchorS);
      if (polys && polys.length) {
        // Bold: expand glyph outlines slightly
        let bold;
        try { bold = offsetPolygons(polys, rl.sz * 0.025); } catch(e) { bold = polys; }
        if (!bold || !bold.length) bold = polys;
        // Clip to viewport
        let clipped;
        try { clipped = intersectPolygons(bold, exportClipRect); } catch(e) { clipped = bold; }
        if (clipped && clipped.length) {
          allRoadLabelPolys.push(...clipped);
          try {
            const expanded = offsetPolygons(bold, rl.sz * 0.3);
            if (expanded.length) roadLabelKnockoutPolys.push(...expanded);
          } catch(e) {}
        }
      }
    }
    // Subtract place label backer rects from road label polys
    if (allRoadLabelPolys.length && exportLabelBackerRects.length) {
      try { allRoadLabelPolys = differencePolygons(allRoadLabelPolys, exportLabelBackerRects); } catch(e) {}
    }
    // Subtract compass backer from road label polys
    if (allRoadLabelPolys.length && layers.compassBacker && layers.compassBacker.length) {
      try { allRoadLabelPolys = differencePolygons(allRoadLabelPolys, layers.compassBacker); } catch(e) {}
    }
    if (allRoadLabelPolys.length) {
      roadLabelPaths = '<path d="' + polygonsToPathD(allRoadLabelPolys) + '" fill="#594a42" fill-rule="evenodd"/>';
    }
  }
  // Cut knockouts from export roads using actual rendered label shapes
  if (exportRoads.length && roadLabelKnockoutPolys.length) {
    try { exportRoads = differencePolygons(exportRoads, roadLabelKnockoutPolys); } catch(e) {}
  }

  // Compass SVG (already paths, just render as-is)
  const compass = compassSvg(w, h, px, py, true); // skip backer - already merged into land

  // Same evenly-spread color assignment as the preview, so exported panels
  // match what's shown on screen regardless of which specific bands are checked.
  const bandColors = getBandColors();

  const panels = [{
    inner: '<rect x="'+(px-hW)+'" y="'+(py-hH)+'" width="'+w+'" height="'+h+'" fill="'+bandColors.baseColor+'"/>',
    label: 'DEEPEST WATER — BASE'
  }];

  // Helper to build a band panel with the frame-inner clip already applied
  function pushBandPanel(bandHolesArr, color, label) {
    if (!bandHolesArr || !bandHolesArr.length) return;
    let bandHoles = bandHolesArr;
    if (layers.ft > 0) {
      const innerR = [[{x:px-hW+layers.ft,y:py-hH+layers.ft},{x:px+hW-layers.ft,y:py-hH+layers.ft},{x:px+hW-layers.ft,y:py+hH-layers.ft},{x:px-hW+layers.ft,y:py+hH-layers.ft}]];
      bandHoles = intersectPolygons(bandHoles, innerR);
    }
    panels.push({
      inner: '<path d="' + bandPathD(bandHoles, w, h, px, py) + '" fill="' + color + '" fill-rule="evenodd"/>',
      label: label
    });
  }

  // Paint order: deepest band first, shallowest last.
  pushBandPanel(layers.band5Holes, bandColors.band5Color, 'VERY DEEP WATER — CUT');
  pushBandPanel(layers.band4Holes, bandColors.band4Color, 'DEEP WATER — CUT');
  pushBandPanel(layers.outerHoles, bandColors.band3Color, 'OUTER WATER — CUT');
  pushBandPanel(layers.midHoles,   bandColors.band2Color, 'MID WATER — CUT');
  pushBandPanel(layers.shallowHoles, bandColors.band1Color, 'SHALLOW WATER — CUT');

  // Fallback: if no text paths were generated, use <text> elements
  if (!titlePaths && (titleText || subtitleText)) {
    titlePaths = titleSvg(w, h, px, py);
  }
  // Labels: if font didn't load, labelPaths stays empty (no <text> fallback)

  // Land panel: merged land path + roads + compass + text paths
  // Knock out title offset and compass from roads
  if (titleKnockout && titleKnockout.length && exportRoads.length) {
    try { exportRoads = differencePolygons(exportRoads, titleKnockout); } catch(e) {}
  }
  if (layers.compassBacker && layers.compassBacker.length && exportRoads.length) {
    try { exportRoads = differencePolygons(exportRoads, layers.compassBacker); } catch(e) {}
  }
  const roadsPath = exportRoads.length ? '<path d="' + polygonsToPathD(exportRoads) + '" fill="#594a42"/>' : '';

  panels.push({
    inner: '<path d="' + polygonsToPathD(exportLand) + '" fill="#ded8c5" fill-rule="evenodd"/>' + roadsPath + roadLabelPaths + labelPaths + compass + titlePaths,
    label: 'LAND — CUT + ENGRAVE'
  });

  const step = w + gap;
  const totalW = panels.length * w + (panels.length - 1) * gap + pad * 2;
  const totalH = h + labelGap + fontSize2 + pad * 2;
  const scaleRatio = (outW || w) / w;
  const body = panels.map((panel, i) => {
    const cx = pad + i * step + hW;
    return '  <g transform="translate(' + cx + ',' + (pad + hH) + ')">' + panel.inner + '</g>\n  <text x="' + cx + '" y="' + (pad + h + labelGap) + '" text-anchor="middle" font-family="Inter,system-ui,sans-serif" font-size="' + fontSize2 + '" fill="#333">' + panel.label + '</text>';
  }).join('\n');

  // Store panels for individual layer download
  _lastExportPanels = panels.map(p => ({ label: p.label, inner: p.inner }));
  _lastExportDims = { w, h, px, py, outW: outW || w, outH: outH || h, scaleRatio, hW, hH };

  return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + totalW + ' ' + totalH + '" width="' + (totalW * scaleRatio).toFixed(1) + 'mm" height="' + (totalH * scaleRatio).toFixed(1) + 'mm">\n' + body + '\n</svg>';
}

// Storage for individual layer export
let _lastExportPanels = null;
let _lastExportDims = null;

// Build a single layer SVG with proper dimensions
function buildSingleLayerSvg(panelIndex) {
  if (!_lastExportPanels || !_lastExportDims) return null;
  const panel = _lastExportPanels[panelIndex];
  if (!panel) return null;
  const { w, h, px, py, scaleRatio, hW, hH } = _lastExportDims;
  return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="' + (px-hW) + ' ' + (py-hH) + ' ' + w + ' ' + h + '" width="' + (w * scaleRatio).toFixed(1) + 'mm" height="' + (h * scaleRatio).toFixed(1) + 'mm">\n' + panel.inner + '\n</svg>';
}

/* =========================================================================
   VISIBLE MAPLIBRE MAP
   ========================================================================= */

const mapContainer = document.getElementById('map-container');
const svgView = document.getElementById('svg-view');
const svgContainer = document.getElementById('map-svg-container');
const backToMapBtn = document.getElementById('back-to-map');

// Set initial preview box size
(function() {
  var f = document.getElementById('board-frame');
  var w = f ? f.clientWidth || 600 : 600;
  var box = document.getElementById('preview-box');
  if (box) {
    var aspect0 = 1; // 10x10 default = square
    var h0 = Math.round(w * aspect0);
    if (h0 > 850) { h0 = 850; w = Math.round(850 / aspect0); box.style.width = w + 'px'; }
    box.style.height = Math.max(300, h0) + 'px';
  }
})();

let glMap;
try {
  glMap = new maplibregl.Map({
    container: 'map-container',
    style: 'https://tiles.openfreemap.org/styles/liberty',
    center: [-97.14, 49.89],
    zoom: 10,
    attributionControl: false,
  });
  glMap.addControl(new maplibregl.NavigationControl(), 'top-right');
} catch (e) {
  document.getElementById('map-container').innerHTML = '<div style="padding:40px;text-align:center;color:#888;font-size:14px;">Download this file and open in Chrome/Firefox/Safari.</div>';
}

function showMap() {
  mapContainer.style.display = 'block';
  svgView.style.display = 'none';
  document.querySelectorAll('.hide-in-svg').forEach(el => el.style.display = '');
  document.querySelectorAll('.show-in-svg').forEach(el => el.style.display = 'none');
  document.querySelectorAll('.post-generate-only').forEach(el => el.style.display = 'none');
}
function showSvg() {
  mapContainer.style.display = 'none';
  svgView.style.display = 'flex';
  document.querySelectorAll('.hide-in-svg').forEach(el => el.style.display = 'none');
  document.querySelectorAll('.show-in-svg').forEach(el => el.style.display = '');
  document.querySelectorAll('.post-generate-only').forEach(el => el.style.display = '');
  const nameEl = document.getElementById('place-display-name');
  if (nameEl) nameEl.textContent = placeInput.value.trim();
}
backToMapBtn.addEventListener('click', showMap);

// ── Save JPG ────────────────────────────────────────────────────────
// Captures the preview SVG as a JPG with embedded fonts so title text
// renders correctly on the canvas.
(function() {
  const saveBtn = document.getElementById('save-jpg-btn');
  if (!saveBtn) return;

  // Fetch a TTF from FONT_URLS and return a base64 data-URI string.
  // Caches results so repeated saves don't re-fetch.
  const fontB64Cache = {};
  async function fontToBase64(familyKey) {
    if (fontB64Cache[familyKey]) return fontB64Cache[familyKey];
    const url = FONT_URLS[familyKey];
    if (!url) return null;
    try {
      const resp = await fetch(url);
      if (!resp.ok) return null;
      const buf = await resp.arrayBuffer();
      let binary = '';
      const bytes = new Uint8Array(buf);
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      const b64 = btoa(binary);
      const dataUri = 'data:font/ttf;base64,' + b64;
      fontB64Cache[familyKey] = dataUri;
      return dataUri;
    } catch (e) {
      console.warn('Font fetch for JPG failed:', familyKey, e);
      return null;
    }
  }

  // Extract the first font-family name from a CSS value like "'Great Vibes',cursive"
  function firstFontName(cssFamily) {
    return cssFamily.split(',')[0].replace(/['"]/g, '').trim();
  }

  saveBtn.addEventListener('click', async function() {
    const svgEl = svgContainer.querySelector('svg');
    if (!svgEl) return;

    saveBtn.disabled = true;
    const origText = saveBtn.innerHTML;
    saveBtn.innerHTML = '⏳ Saving…';

    try {
      // Clone the SVG so we don't mutate the live preview
      const clone = svgEl.cloneNode(true);

      // Collect unique font-family keys used in the SVG
      const usedFonts = new Set();
      clone.querySelectorAll('[font-family]').forEach(el => {
        const ff = el.getAttribute('font-family');
        if (ff && FONT_URLS[ff]) usedFonts.add(ff);
      });
      // Also check the Inter font used for subtitles/labels
      if (FONT_URLS['Inter,Helvetica,sans-serif']) {
        clone.querySelectorAll('[font-family]').forEach(el => {
          const ff = el.getAttribute('font-family');
          if (ff && ff.indexOf('Inter') !== -1) usedFonts.add('Inter,Helvetica,sans-serif');
        });
      }
      // Always include the currently selected title font
      if (typeof titleFont !== 'undefined' && FONT_URLS[titleFont]) usedFonts.add(titleFont);

      // Build @font-face rules with embedded base64 TTF data
      let fontFaceCSS = '';
      for (const fKey of usedFonts) {
        const dataUri = await fontToBase64(fKey);
        if (!dataUri) continue;
        const name = firstFontName(fKey);
        fontFaceCSS += '@font-face { font-family: \'' + name + '\'; src: url("' + dataUri + '") format("truetype"); }\n';
      }

      // Inject the font-face CSS into the cloned SVG
      if (fontFaceCSS) {
        const defsEl = clone.querySelector('defs') || clone.insertBefore(document.createElementNS('http://www.w3.org/2000/svg', 'defs'), clone.firstChild);
        const styleEl = document.createElementNS('http://www.w3.org/2000/svg', 'style');
        styleEl.textContent = fontFaceCSS;
        defsEl.appendChild(styleEl);
      }

      // Set explicit dimensions on the clone
      const box = svgEl.getBoundingClientRect();
      const w = Math.round(box.width);
      const h = Math.round(box.height);
      clone.setAttribute('width', w);
      clone.setAttribute('height', h);

      // Serialize to a blob URL (avoids data-URI size limits)
      const svgStr = new XMLSerializer().serializeToString(clone);
      const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
      const blobUrl = URL.createObjectURL(blob);

      // Draw on canvas
      const img = new Image();
      img.onload = function() {
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(blobUrl);

        // Build filename from place name
        let fname = 'map-preview';
        const nameEl = document.getElementById('place-display-name');
        if (nameEl && nameEl.textContent.trim()) {
          fname = nameEl.textContent.trim().replace(/[^a-zA-Z0-9 _-]/g, '').replace(/\s+/g, '-');
        }

        // Trigger download
        canvas.toBlob(function(jpgBlob) {
          const a = document.createElement('a');
          a.href = URL.createObjectURL(jpgBlob);
          a.download = fname + '-preview.jpg';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(a.href);

          saveBtn.disabled = false;
          saveBtn.innerHTML = origText;
        }, 'image/jpeg', 0.92);
      };
      img.onerror = function() {
        URL.revokeObjectURL(blobUrl);
        console.error('Failed to render SVG to canvas for JPG export');
        saveBtn.disabled = false;
        saveBtn.innerHTML = origText;
      };
      img.src = blobUrl;

    } catch (err) {
      console.error('Save JPG error:', err);
      saveBtn.disabled = false;
      saveBtn.innerHTML = origText;
    }
  });
})();
// ── End Save JPG ────────────────────────────────────────────────────

function mapWidthKm() {
  if (!glMap) return 10;
  const bounds = glMap.getBounds();
  const lat = glMap.getCenter().lat;
  const rawKm = (bounds.getEast() - bounds.getWest()) * 111.320 * Math.cos(lat * Math.PI / 180);
  // getBounds() returns the axis-aligned bounding box of the viewport.
  // When the map is rotated, that box is wider than the actual viewport.
  // Correct by dividing out the expansion: for a rectangle rotated by θ,
  // bounding-box width = viewport_w·cos(θ) + viewport_h·sin(θ).
  // With aspect = h/w: bounding_w = viewport_w · (cos(θ) + aspect·|sin(θ)|)
  const bearing = glMap.getBearing() || 0;
  if (Math.abs(bearing) < 0.5) return rawKm;
  const theta = Math.abs(bearing) * Math.PI / 180;
  const aspect = (mapContainer.clientHeight || 400) / (mapContainer.clientWidth || 600);
  return rawKm / (Math.cos(theta) + aspect * Math.sin(theta));
}

// Temporarily enlarges the (hidden, behind the loading overlay) map canvas
// and jumps it to a fixed high zoom, centered on the same point the user
// framed, so MapLibre loads full-detail tiles across the whole area the user
// intended to capture -- instead of whatever detail happened to be loaded at
// their on-screen zoom. Capped on two axes so a huge framed area degrades
// gracefully (lower effective zoom) instead of requesting a huge tile set:
// DETAIL_CANVAS_MAX_PX bounds the temporary canvas size, DETAIL_WAIT_MS bounds
// how long we wait for tiles. Returns a restore() function that puts the live
// map back exactly where the user left it.
async function boostMapDetail(widthKm, aspect, onStatus) {
  const noop = () => {};
  if (!glMap) return noop;

  const originalCenter = glMap.getCenter();
  const originalZoom = glMap.getZoom();
  const originalBearing = glMap.getBearing();
  const lat = originalCenter.lat;
  const originalDisplay = mapContainer.style.display;
  const originalWidth = mapContainer.style.width;
  const originalHeight = mapContainer.style.height;

  // Required real-world coverage, derived from the SAME rect math
  // computeBands itself uses -- not a separately-tuned guess that could
  // silently fall out of sync with it. This part ALWAYS runs, regardless of
  // whether we also raise zoom for extra road detail below. A previous
  // version of this function skipped this ENTIRE calculation whenever the
  // area was already road-dense or the user was already zoomed in past the
  // detail target -- which meant water/land extraction fell back to
  // whatever tiles the raw on-screen viewport happened to have loaded, with
  // no guarantee that covered the full area the band computation needs.
  // Missing data at that boundary reads as "land with nothing beyond it",
  // and the band-offset step then draws a false ring outward from that fake
  // edge -- the "offset created from nowhere at the edge of the screen" bug.
  const REF_SIZE_LOCAL = 500;
  const maxBandOffsetLocal = (BAND_1_MM + BAND_2_MM + BAND_3_MM + BAND_4_MM + BAND_5_MM) * 2;
  const viewHalfLocal = REF_SIZE_LOCAL * 0.75 + maxBandOffsetLocal;
  const safeAspect = (aspect && isFinite(aspect) && aspect > 0) ? aspect : 1;
  const halfXUnits = viewHalfLocal;
  // Must exactly match computeBands' own viewHalfY formula (REF_SIZE * aspect
  // * 0.75 + maxBandOffset) -- an earlier version of this line used
  // REF_SIZE*aspect/2 (missing the 0.75x margin and +maxBandOffset that the
  // X-axis, and now the Y-axis in computeBands, both carry), which under-
  // covers tall outputs relative to what the compute rect actually needs,
  // even after the compute rect itself was made aspect-aware.
  const halfYUnits = Math.max(viewHalfLocal, REF_SIZE_LOCAL * safeAspect * 0.75 + maxBandOffsetLocal);
  // A bit of slack on top of the exact requirement -- tiles load in whole
  // units, so it's better to over-cover slightly than to clip an edge. Also
  // hedges against the tile-detail differences noted across browsers
  // (devicePixelRatio affects exactly how much ground a given CSS pixel
  // covers), which the exact math here can't fully account for.
  const SLACK = 1.3;
  // No extra expansion for rotation: the temporary canvas is rotated together
  // with the map (jumpTo passes the bearing), and extracted features are
  // rotated into that same frame by toMm, so a canvas of this size already
  // covers exactly the rotated rectangle we need. Inflating the span for
  // bearing only forced a lower tile zoom, which dropped minor/service roads.
  const spanXm = (halfXUnits * 2 / REF_SIZE_LOCAL) * widthKm * 1000 * SLACK;
  const spanYm = (halfYUnits * 2 / REF_SIZE_LOCAL) * widthKm * 1000 * SLACK;

  function metersPerPixel(zoom) { return 156543.03392 * Math.cos(lat * Math.PI / 180) / Math.pow(2, zoom); }

  // Whether to ALSO raise zoom for extra road/label detail is the ONLY
  // thing the road-density check (and the "already zoomed in" check) may
  // affect. It must never affect whether coverage itself gets ensured.
  let targetZoom = originalZoom;
  let wantsMoreDetail = originalZoom < DETAIL_ZOOM_TARGET;
  if (wantsMoreDetail) {
    try {
      const alreadyLoaded = glMap.querySourceFeatures('openmaptiles', { sourceLayer: 'transportation' });
      if (alreadyLoaded && alreadyLoaded.length >= DETAIL_SKIP_ROAD_COUNT) wantsMoreDetail = false;
    } catch (e) { /* if this check fails for any reason, fall through and still try to raise detail */ }
  }
  if (wantsMoreDetail) targetZoom = DETAIL_ZOOM_TARGET;

  let requiredPxW = spanXm / metersPerPixel(targetZoom);
  let requiredPxH = spanYm / metersPerPixel(targetZoom);

  // Cap: step the target zoom down until both dimensions fit, but never
  // below the zoom the user was already viewing at. When wantsMoreDetail is
  // already false this loop is a no-op (targetZoom already equals
  // originalZoom, its own floor), so the cap only ever trims the DETAIL
  // raise, never the coverage guarantee itself below originalZoom.
  while (Math.max(requiredPxW, requiredPxH) > DETAIL_CANVAS_MAX_PX && targetZoom > originalZoom) {
    targetZoom -= 1;
    requiredPxW = spanXm / metersPerPixel(targetZoom);
    requiredPxH = spanYm / metersPerPixel(targetZoom);
  }

  requiredPxW = Math.min(DETAIL_CANVAS_MAX_PX, Math.round(requiredPxW));
  requiredPxH = Math.min(DETAIL_CANVAS_MAX_PX, Math.round(requiredPxH));

  // If we're not raising zoom for extra detail AND the on-screen container
  // is already at least as big as what's required, the current view already
  // provides adequate coverage -- nothing to do. (Only trusted when the
  // container is actually visible; mid-regenerate while viewing the SVG
  // result it reads as 0x0 regardless of its real size, so this check is
  // skipped in that case and coverage gets ensured unconditionally instead.)
  if (!wantsMoreDetail && originalDisplay !== 'none') {
    const currentW = mapContainer.clientWidth || 0;
    const currentH = mapContainer.clientHeight || 0;
    if (currentW >= requiredPxW && currentH >= requiredPxH) return noop;
  }

  if (onStatus) onStatus(wantsMoreDetail ? 'Loading extra detail\u2026' : 'Loading full coverage\u2026');

  mapContainer.style.display = 'block';
  mapContainer.style.width = requiredPxW + 'px';
  mapContainer.style.height = requiredPxH + 'px';
  glMap.resize();
  glMap.jumpTo({ center: originalCenter, zoom: targetZoom, bearing: originalBearing });

  // Always wait for the next 'idle' cycle here -- do NOT shortcut with an
  // immediate glMap.loaded()/isSourceLoaded() check the way the OTHER wait
  // in the generate handler does. That shortcut is fine there because it's
  // checking tiles from the user's own pan/zoom, which happened over real
  // time. Here it's checked in the same tick as our OWN resize()/jumpTo()
  // just above -- MapLibre updates loaded()/isSourceLoaded() as part of its
  // render loop, not synchronously when those methods are called, so this
  // could read the STALE "still loaded from before the resize" state and
  // resolve immediately, skipping the wait entirely and leaving extraction
  // to run against the old (insufficient) tile set. This is intermittent by
  // nature -- it depends on browser scheduling timing -- which is exactly
  // why the coverage bug got much rarer after the last fix but not gone.
  await new Promise((resolve) => {
    let resolved = false;
    const finish = () => { if (resolved) return; resolved = true; glMap.off('idle', onIdle); resolve(); };
    const onIdle = () => finish();
    glMap.on('idle', onIdle);
    setTimeout(finish, DETAIL_WAIT_MS);
  });

  return function restore() {
    mapContainer.style.width = originalWidth;
    mapContainer.style.height = originalHeight;
    mapContainer.style.display = originalDisplay;
    glMap.resize();
    glMap.jumpTo({ center: originalCenter, zoom: originalZoom, bearing: originalBearing });
  };
}

/* =========================================================================
   UI WIRING
   ========================================================================= */

const placeInput = document.getElementById('place-input');
const generateBtn = document.getElementById('generate-btn');
const statusEl = document.getElementById('status');
const engraveSeg = document.getElementById('engrave-seg');
const unitSeg = document.getElementById('unit-seg');
const sizeHint = document.getElementById('size-hint');
const dlBtn = document.getElementById('dl-full');

let roadMode = 'all', bandScale = 1.0, useInches = true;
let roadThickMult = 1.6;
let mapBearing = 0; // map rotation in degrees (0 = north up)
let showStreams = false; // toggle small streams/creeks in water layer
let showTinyLakes = false; // toggle very small water bodies
let showLabel = true, showFrame = true, frameThickness = 0.02, compassCorner = 'tl';
let compassSzMult = 1.0;
let compassOffX = 0, compassOffY = 0; // fine-tuning offsets from corner position
// Which depth bands are active, keyed by band number (1 = shallowest/#3 in the
// UI, 5 = deepest/#7). Independent checkboxes: any combination is allowed,
// not just a contiguous prefix. Default matches the old layerCount=5 default
// (bands 1, 2, 3 active; 4 and 5 off).
let enabledBands = { 1: true, 2: true, 3: true, 4: false, 5: false };
let labelSzMult = 1.0;
// Road name labels (curved street names on major roads). Defaults off --
// flip to true once you've checked it against a few real places, or just
// change the default .on button in the HTML control below.
let showRoadLabels = false;
let roadLabelSzMult = 0.7;
let roadLabelFlip = false;
let titleFont = "'Great Vibes',cursive", titleAlign = 'left', titleOffsetOn = true, titleBackerStyle = 'offset' /* always on */, titleDx = 0, titleDy = 0;
let titleSzMult = 1.0, subSzMult = 1.0, subDx = 0, lineGap = 1.3, rectPadMult = 1.0;
let dimW = DEFAULT_W_IN, dimH = DEFAULT_H_IN;
let lastResult = null, featureCache = null, rawFeatureCache = null, lastWidthKm = null;
const bandCache = {}; // cache computed bands per bandScale value

function toMmDim(v) { return useInches ? v * INCHES_TO_MM : v; }
function dimUnit() { return useInches ? 'in' : 'mm'; }
function dimMin() { return useInches ? MIN_DIM_IN : MIN_DIM_MM; }
function dimMax() { return useInches ? MAX_DIM_IN : MAX_DIM_MM; }
function dimStep() { return useInches ? STEP_IN : STEP_MM; }
function setStatus(msg, type) { statusEl.textContent = msg || ''; statusEl.className = 'status' + (type ? ' ' + type : ''); }

function updateMapAspect() {
  const aspect = toMmDim(dimH) / toMmDim(dimW);
  const frame = document.getElementById('board-frame');
  const maxW = (frame && frame.clientWidth) || 600;
  let w = maxW, h = Math.round(w * aspect);
  // If height exceeds 850px, shrink width to keep aspect ratio within 850px
  if (h > 850) {
    h = 850;
    w = Math.round(h / aspect);
  }
  h = Math.max(300, h);
  const box = document.getElementById('preview-box');
  if (box) {
    box.style.height = h + 'px';
    box.style.width = w < maxW ? w + 'px' : '100%';
  }
  if (glMap) glMap.resize();
  syncPanelHeight();
}

// Keep the side panel exactly as tall as the map area. The panel scrolls
// internally when its controls don't fit, so it never makes the map frame
// taller than the map itself. In the stacked (mobile) layout the panel is
// left to size itself.
(function () {
  const s = document.createElement('style');
  s.textContent =
    '#mapgen-app .input-panel{box-sizing:border-box;overflow-y:auto;scrollbar-width:thin;scrollbar-color:rgba(255,255,255,0.25) transparent;}' +
    '#mapgen-app .input-panel > *{flex-shrink:0;}';
  document.head.appendChild(s);
})();
function syncPanelHeight() {
  const widget = document.querySelector('#mapgen-app .widget');
  const panel = document.querySelector('#mapgen-app .input-panel');
  const frame = document.getElementById('board-frame');
  const box = document.getElementById('preview-box');
  if (!widget || !panel || !frame || !box) return;
  if (getComputedStyle(widget).flexDirection === 'column') {
    panel.style.height = '';
    return;
  }
  const cs = getComputedStyle(frame);
  const h = box.offsetHeight
    + parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom)
    + parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
  panel.style.height = h + 'px';
}
window.addEventListener('resize', syncPanelHeight);

// Fly to place
async function flyToPlace(name) {
  setStatus('Finding the place\u2026');
  try {
    const geo = await geocodePlace(name, msg => setStatus(msg));
    // Reset rotation when navigating to a new place
    mapBearing = 0;
    const rotSlider = document.getElementById('rotation-slider');
    const rotVal = document.getElementById('rotation-val');
    if (rotSlider) rotSlider.value = 0;
    if (rotVal) rotVal.textContent = '0°';

    if (geo.extentKm) {
      const fitKm = Math.max(geo.extentKm.width, geo.extentKm.height) * 1.3;
      const zoom = Math.log2(156543.03 * Math.cos(geo.lat * Math.PI / 180) * (mapContainer.clientWidth || 600) / (fitKm * 1000));
      glMap.flyTo({ center: [geo.lon, geo.lat], zoom: Math.max(3, zoom), bearing: 0, duration: 1500 });
    } else {
      glMap.flyTo({ center: [geo.lon, geo.lat], zoom: 12, bearing: 0, duration: 1500 });
    }
    setStatus('Adjust the view, then click Generate.', 'ok');
  } catch (err) { setStatus(err.message, 'error'); }
}

document.getElementById('go-btn').addEventListener('click', () => {
  const n = placeInput.value.trim();
  if (n) { showMap(); flyToPlace(n); }
});

document.getElementById('go-btn').addEventListener('click', () => {
  const n = placeInput.value.trim();
  if (n) { showMap(); flyToPlace(n); }
});

placeInput.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') { const n = placeInput.value.trim(); if (n) { showMap(); flyToPlace(n); } }
});

// Seg handlers
function setupSeg(id, callback) {
  document.getElementById(id).addEventListener('click', (e) => {
    const t = e.target.closest('.sb'); if (!t) return;
    document.getElementById(id).querySelectorAll('.sb').forEach(s => s.classList.toggle('on', s === t));
    callback(t.dataset.value);
  });
}
setupSeg('engrave-seg', v => { roadMode = v; rerenderSvgOnly(); });

// Label size +/- buttons. The backer/knockout merge only affects the EXPORT
// (which always rebuilds layers fresh), so for preview speed we just update
// the overlay without rebuilding layers. Complex maps with hundreds of lakes
// take seconds per full rebuild — overlay-only is instant.
(function () {
  const minusBtn = document.getElementById('label-sz-minus');
  const plusBtn = document.getElementById('label-sz-plus');
  if (minusBtn) minusBtn.addEventListener('click', () => {
    labelSzMult = Math.max(0.4, +(labelSzMult - 0.1).toFixed(1));
    rerenderSvgOnly();

  });
  if (plusBtn) plusBtn.addEventListener('click', () => {
    labelSzMult = Math.min(2.5, +(labelSzMult + 0.1).toFixed(1));
    rerenderSvgOnly();
  });
}());

// Road-label size +/- buttons (separate from place-label size).
(function () {
  const minusBtn = document.getElementById('road-label-sz-minus');
  const plusBtn = document.getElementById('road-label-sz-plus');
  if (minusBtn) minusBtn.addEventListener('click', () => {
    roadLabelSzMult = Math.max(0.3, +(roadLabelSzMult - 0.1).toFixed(1));
    rerenderSvgOnly();
  });
  if (plusBtn) plusBtn.addEventListener('click', () => {
    roadLabelSzMult = Math.min(3.0, +(roadLabelSzMult + 0.1).toFixed(1));
    rerenderSvgOnly();
  });
}());

// Compass size +/- buttons. Same as labels — export rebuilds fresh.
(function () {
  const minusBtn = document.getElementById('compass-sz-minus');
  const plusBtn = document.getElementById('compass-sz-plus');
  if (minusBtn) minusBtn.addEventListener('click', () => {
    compassSzMult = Math.max(0.4, +(compassSzMult - 0.1).toFixed(1));
    rerenderSvgOnly();
  });
  if (plusBtn) plusBtn.addEventListener('click', () => {
    compassSzMult = Math.min(2.5, +(compassSzMult + 0.1).toFixed(1));
    rerenderSvgOnly();
  });
}());
// Road thickness +/- buttons. Because preview roads are STROKED (see
// buildFullSvg), width changes reflow instantly via rerenderSvgOnly with no
// layer rebuild. If these button IDs aren't in the HTML the query is a no-op.
(function () {
  const thinBtn = document.getElementById('road-thin');
  const thickBtn = document.getElementById('road-thick');
  if (thinBtn) thinBtn.addEventListener('click', () => {
    roadThickMult = Math.max(0.3, +(roadThickMult - 0.2).toFixed(1));
    rerenderSvgOnly();
  });
  if (thickBtn) thickBtn.addEventListener('click', () => {
    roadThickMult = +(roadThickMult + 0.2).toFixed(1);
    rerenderSvgOnly();
  });
}());
// Depth layer checkboxes. Numbering now matches the physical stack order:
// 1 = Land (always present, not togglable), 2-6 = the 5 water bands
// (shallowest to deepest, independently checkable), 7 = Base/deepest water
// (always present, not togglable). Band index = label value - 1 for the
// checkable range (2-6 -> band 1-5). Land/Base are rendered as fixed,
// non-interactive pills so they read as context, not options.
// A checkmark glyph is added/removed from the label text itself (not just a
// background/border color change) so the checked state reads unambiguously.
(function () {
  const depthSeg = document.getElementById('depth-seg');
  if (!depthSeg) return;

  function paint(el, bandIdx) {
    const baseLabel = el.dataset.label || el.textContent.replace(/^\u2713\s*/, '');
    el.dataset.label = baseLabel;
    const on = !!enabledBands[bandIdx];
    el.classList.toggle('on', on);
    el.textContent = on ? ('\u2713 ' + baseLabel) : baseLabel;
  }

  // Reflect the default enabledBands state onto the checkboxes on load.
  // Fixed pills (Land=1, Base=7) are skipped entirely — they have no
  // enabledBands entry and are never clickable.
  depthSeg.querySelectorAll('.sb').forEach(el => {
    const v = parseInt(el.dataset.value, 10);
    if (v === 1 || v === 7) return;
    paint(el, v - 1);
  });

  depthSeg.addEventListener('click', (e) => {
    const t = e.target.closest('.sb');
    if (!t) return;
    const v = parseInt(t.dataset.value, 10);
    if (v === 1 || v === 7) return; // Land / Base are fixed, not toggles
    const bandIdx = v - 1;
    enabledBands[bandIdx] = !enabledBands[bandIdx];
    paint(t, bandIdx);
    Object.keys(layerCache).forEach(k => delete layerCache[k]);
    rerender();
  });
}());
setupSeg('label-seg', v => { showLabel = v === 'on'; rerenderSvgOnly(); });
// Stream toggle: recompute water from cached pieces without re-extracting
// Shared helper for stream/tiny-lake toggles — shows loading overlay,
// yields to the browser for a paint, then recomputes.
// Caches union results per toggle combination so repeated toggles are instant.


function rebuildWaterFromCache() {
  if (!rawFeatureCache) return;
  const overlay = document.getElementById('loading-overlay');
  if (overlay) overlay.style.display = 'flex';
  setTimeout(() => {
    const waterPolys = showTinyLakes
      ? [...rawFeatureCache.bigWaterPolys, ...rawFeatureCache.smallLakePolys]
      : rawFeatureCache.bigWaterPolys;
    // No union — just concatenate. ClipperLib NonZero fill handles overlaps
    // in computeBands. This makes stream/lake toggles near-instant.
    const base = [...waterPolys, ...rawFeatureCache.riverPolys];
    rawFeatureCache.waterBase = base;
    rawFeatureCache.allWaterRaw = (showStreams && rawFeatureCache.streamPolys && rawFeatureCache.streamPolys.length)
      ? [...base, ...rawFeatureCache.streamPolys] : base;
    Object.keys(bandCache).forEach(k => delete bandCache[k]);
    Object.keys(layerCache).forEach(k => delete layerCache[k]);
    const widthMm = toMmDim(dimW), heightMm = toMmDim(dimH);
    featureCache = computeBands(rawFeatureCache, null, heightMm / widthMm);
    bandCache[bandScale.toFixed(2)] = featureCache;
    rerender();
    if (overlay) overlay.style.display = 'none';
  }, 30);
}
if (document.getElementById('stream-seg')) {
  setupSeg('stream-seg', v => {
    showStreams = v === 'on';
    rebuildWaterFromCache();
  });
}
if (document.getElementById('tiny-lakes-seg')) {
  setupSeg('tiny-lakes-seg', v => {
    showTinyLakes = v === 'on';
    rebuildWaterFromCache();
  });
}
// Road name labels toggle. Guarded (unlike the other setupSeg calls above)
// because setupSeg itself has no null check and this is a new control --
// until the matching HTML is added, this is a harmless no-op instead of a
// page-breaking error on load.
if (document.getElementById('road-label-seg')) {
  setupSeg('road-label-seg', v => {
    showRoadLabels = v === 'on';
    // First time turning on: cached layers have no roadLabels (buildLayers
    // skipped them when labels were off). Need one full rebuild to compute
    // placements. After that, toggling off/on reuses the cached data.
    if (showRoadLabels && lastResult && lastResult.layers && (!lastResult.layers.roadLabels || !lastResult.layers.roadLabels.length)) {
      Object.keys(layerCache).forEach(k => delete layerCache[k]);
      rerender();
    } else {
      rerenderSvgOnly();
    }
  });
}
if (document.getElementById('road-label-flip-seg')) {
  setupSeg('road-label-flip-seg', v => { roadLabelFlip = v === 'flip'; rerenderSvgOnly(); });
}
setupSeg('offset-seg', async v => {
  bandScale = parseFloat(v);
  if (!rawFeatureCache) return;
  const cacheKey = bandScale.toFixed(2);
  if (bandCache[cacheKey]) {
    featureCache = bandCache[cacheKey];
  } else {
    document.getElementById('loading-overlay').style.display = 'flex';
    document.getElementById('loading-msg').textContent = 'Computing offset...';
    await new Promise(r => setTimeout(r, 50));
    featureCache = computeBands(rawFeatureCache, msg => { document.getElementById('loading-msg').textContent = msg; }, toMmDim(dimH) / toMmDim(dimW));
    bandCache[cacheKey] = featureCache;
    document.getElementById('loading-overlay').style.display = 'none';
  }
  Object.keys(layerCache).forEach(k => delete layerCache[k]);
  rerender();
  showSvg();
});
setupSeg('compass-seg', v => { compassCorner = v; compassOffX = 0; compassOffY = 0; rerenderSvgOnly(); });
if (document.getElementById('compass-seg-off')) {
  setupSeg('compass-seg-off', v => { compassCorner = v; compassOffX = 0; compassOffY = 0; rerenderSvgOnly(); });
}

// Compass d-pad for fine-tuning position from the corner preset
// Uses setupHoldBtn (same as title d-pad) — holds down to move continuously
(function () {
  const step = 3;
  if (document.getElementById('compass-up')) setupHoldBtn('compass-up', () => { compassOffY -= step; });
  if (document.getElementById('compass-down')) setupHoldBtn('compass-down', () => { compassOffY += step; });
  if (document.getElementById('compass-left')) setupHoldBtn('compass-left', () => { compassOffX -= step; });
  if (document.getElementById('compass-right')) setupHoldBtn('compass-right', () => { compassOffX += step; });
  const resetBtn = document.getElementById('compass-reset');
  if (resetBtn) resetBtn.addEventListener('click', () => { compassOffX = 0; compassOffY = 0; rerenderSvgOnly(); });
}());
// Frame +/- stepper
const frameSteps = [{label:'Off',val:0},{label:'Thin',val:0.01},{label:'Normal',val:0.02},{label:'Wide',val:0.03},{label:'Extra',val:0.04}];
let frameIdx = 2;
function updateFrameDisplay() {
  document.getElementById('frame-val').textContent = frameSteps[frameIdx].label;
  frameThickness = frameSteps[frameIdx].val;
  showFrame = frameIdx > 0;
  rerenderSvgOnly();
}
document.getElementById('frame-minus').addEventListener('click', () => { frameIdx = Math.max(0, frameIdx - 1); updateFrameDisplay(); });
document.getElementById('frame-plus').addEventListener('click', () => { frameIdx = Math.min(frameSteps.length - 1, frameIdx + 1); updateFrameDisplay(); });

// Title/subtitle update on input
let titleDebounce = null;
function debouncedRerender() { if (titleDebounce) clearTimeout(titleDebounce); titleDebounce = setTimeout(rerenderSvgOnly, 300); }
document.getElementById('title-input').addEventListener('input', debouncedRerender);
document.getElementById('font-select').addEventListener('change', (e) => { titleFont = e.target.value; rerenderSvgOnly(); });
document.getElementById('pad-minus').addEventListener('click', () => { rectPadMult = Math.max(0.3, rectPadMult - 0.2); rerenderSvgOnly(); });
document.getElementById('pad-plus').addEventListener('click', () => { rectPadMult = Math.min(3.0, rectPadMult + 0.2); rerenderSvgOnly(); });
setupSeg('title-backer-seg', v => { titleBackerStyle = v; titleOffsetOn = v === 'offset'; rerenderSvgOnly(); });
document.getElementById('title-sz-minus').addEventListener('click', () => { titleSzMult = Math.max(0.5, titleSzMult - 0.1); rerenderSvgOnly(); });
document.getElementById('title-sz-plus').addEventListener('click', () => { titleSzMult = +(titleSzMult + 0.1).toFixed(1); rerenderSvgOnly(); });
document.getElementById('sub-sz-minus').addEventListener('click', () => { subSzMult = Math.max(0.5, subSzMult - 0.1); rerenderSvgOnly(); });
document.getElementById('sub-sz-plus').addEventListener('click', () => { subSzMult = Math.min(2.0, subSzMult + 0.1); rerenderSvgOnly(); });
function setupHoldBtn(id, fn) {
  const btn = document.getElementById(id);
  let iv = null;
  const go = () => { fn(); rerenderSvgOnly(); };
  const start = () => { go(); iv = setInterval(go, 120); };
  const stop = () => { if (iv) { clearInterval(iv); iv = null; } };
  btn.addEventListener('mousedown', start);
  btn.addEventListener('touchstart', (e) => { e.preventDefault(); start(); });
  btn.addEventListener('mouseup', stop); btn.addEventListener('mouseleave', stop);
  btn.addEventListener('touchend', stop); btn.addEventListener('touchcancel', stop);
}
setupHoldBtn('sub-left', () => { subDx -= 1; });
setupHoldBtn('sub-right', () => { subDx += 1; });
document.getElementById('gap-minus').addEventListener('click', () => { lineGap = Math.max(0.8, lineGap - 0.1); rerenderSvgOnly(); });
document.getElementById('gap-plus').addEventListener('click', () => { lineGap = Math.min(3.0, lineGap + 0.1); rerenderSvgOnly(); });
setupHoldBtn('title-left', () => { titleDx -= 1; });
setupHoldBtn('title-right', () => { titleDx += 1; });
setupHoldBtn('title-up', () => { titleDy -= 1; });
setupHoldBtn('title-down', () => { titleDy += 1; });
document.getElementById('title-reset').addEventListener('click', () => { titleDx = 0; titleDy = 0; rerenderSvgOnly(); });
document.getElementById('subtitle-input').addEventListener('input', debouncedRerender);

// Dimension controls
function updateDimDisplay() {
  document.getElementById('w-val').textContent = dimW + ' ' + dimUnit();
  document.getElementById('h-val').textContent = dimH + ' ' + dimUnit();
  sizeHint.textContent = 'Map \u2248 ' + dimW + ' \u00d7 ' + dimH + ' ' + dimUnit() + ' when cut.';
}
document.getElementById('w-minus').addEventListener('click', () => { dimW = Math.max(dimMin(), dimW - dimStep()); updateDimDisplay(); updateMapAspect(); });
document.getElementById('w-plus').addEventListener('click', () => { dimW = Math.min(dimMax(), dimW + dimStep()); updateDimDisplay(); updateMapAspect(); });
document.getElementById('h-minus').addEventListener('click', () => { dimH = Math.max(dimMin(), dimH - dimStep()); updateDimDisplay(); updateMapAspect(); });
document.getElementById('h-plus').addEventListener('click', () => { dimH = Math.min(dimMax(), dimH + dimStep()); updateDimDisplay(); updateMapAspect(); });

unitSeg.addEventListener('click', (e) => {
  const t = e.target.closest('.sb'); if (!t) return;
  unitSeg.querySelectorAll('.sb').forEach(s => s.classList.toggle('on', s === t));
  const newIn = t.dataset.value === 'in';
  if (newIn !== useInches) {
    dimW = newIn ? Math.min(MAX_DIM_IN, Math.max(MIN_DIM_IN, Math.round(dimW / INCHES_TO_MM))) : Math.min(MAX_DIM_MM, Math.max(MIN_DIM_MM, Math.round(dimW * INCHES_TO_MM / 10) * 10));
    dimH = newIn ? Math.min(MAX_DIM_IN, Math.max(MIN_DIM_IN, Math.round(dimH / INCHES_TO_MM))) : Math.min(MAX_DIM_MM, Math.max(MIN_DIM_MM, Math.round(dimH * INCHES_TO_MM / 10) * 10));
    useInches = newIn; updateDimDisplay();
  }
});
updateDimDisplay();
setTimeout(updateMapAspect, 100);

// ── Map rotation slider ───────────────────────────────────────────────
// Syncs a range input with MapLibre's bearing. Range -30° to +30°, step 5°.
(function () {
  const slider = document.getElementById('rotation-slider');
  const valEl = document.getElementById('rotation-val');
  const resetBtn = document.getElementById('rotation-reset');
  if (!slider) return;

  function updateDisplay(deg) {
    if (valEl) valEl.textContent = Math.round(deg) + '°';
    slider.value = deg;
  }

  // Slider → map
  // MapLibre's positive bearing rotates the *camera* clockwise, which makes
  // map content appear to rotate counterclockwise. Negate so that sliding
  // right rotates the map visually to the right.
  slider.addEventListener('input', () => {
    const deg = parseFloat(slider.value) || 0;
    mapBearing = deg;
    updateDisplay(deg);
    if (glMap) glMap.setBearing(-deg);
  });

  // Map drag-rotate → slider (MapLibre fires 'rotate' during interaction)
  if (glMap) {
    glMap.on('rotate', () => {
      const deg = -glMap.getBearing(); // negate to match slider convention
      mapBearing = Math.max(-45, Math.min(45, Math.round(deg)));
      updateDisplay(mapBearing);
    });
  }

  // Reset button
  if (resetBtn) resetBtn.addEventListener('click', () => {
    mapBearing = 0;
    updateDisplay(0);
    if (glMap) glMap.setBearing(0);
  });
})();
// ── End rotation slider ───────────────────────────────────────────────

function downloadSvg(str, name) {
  try {
    const blob = new Blob([str], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch (e) {
    // Fallback: open SVG in new tab for manual save
    const w = window.open('', '_blank');
    if (w) { w.document.write('<pre>' + str.replace(/</g, '&lt;') + '</pre>'); }
    else { setStatus('Popup blocked. Allow popups and try again.', 'error'); }
  }
}

// Fast re-render from cached data
function rerenderSvgOnly() {
  if (!lastResult || !featureCache) return;
  const { layers, widthMm, heightMm } = lastResult;
  const refW = featureCache.refSize;
  const refH = featureCache.refSize * (heightMm / widthMm);
  // Try overlay-only update first (much faster for complex maps).
  // If anything goes wrong, fall back to full SVG rebuild so the user
  // never sees a blank/broken map from a silent error.
  try {
    const overlayEl = svgContainer.querySelector('.overlay-layer');
    if (overlayEl) {
      overlayEl.innerHTML = buildOverlaySvg(layers, refW, refH, 0, 0);
      return;
    }
  } catch (e) { console.warn('[overlay rerender] falling back to full rebuild:', e); }
  svgContainer.innerHTML = buildFullSvg(layers, refW, refH, 0, 0);
}

const layerCache = {}; // cache buildLayers results per roadMode+enabledBands key

function bandsCacheKey() {
  return [1, 2, 3, 4, 5].map(k => enabledBands[k] ? '1' : '0').join('');
}

function rerender() {
  if (!featureCache) return;
  const widthMm = toMmDim(dimW), heightMm = toMmDim(dimH);
  const cacheKey = roadMode + '_' + bandsCacheKey() + '_rl' + (showRoadLabels ? '1' : '0');
  let layers;
  if (layerCache[cacheKey]) {
    layers = layerCache[cacheKey];
  } else {
    layers = buildLayers(featureCache, widthMm, heightMm, 0, 0, roadMode);
    layerCache[cacheKey] = layers;
  }
  lastResult = { widthMm, heightMm, panXmm: 0, panYmm: 0, layers };
  const refW = featureCache.refSize;
  const refH = featureCache.refSize * (heightMm / widthMm);
  svgContainer.innerHTML = buildFullSvg(layers, refW, refH, 0, 0);
  dlBtn.disabled = false; var _dli = document.getElementById("dl-individual"); if (_dli) _dli.disabled = false;
}

// Full generate: extract from map + compute bands + render
generateBtn.addEventListener('click', async () => {
  generateBtn.disabled = true; dlBtn.disabled = true;
  document.getElementById('loading-overlay').style.display = 'flex';
  const loadMsg = document.getElementById('loading-msg');
  let restoreDetailBoost = null;
  try {
    loadMsg.textContent = 'Waiting for tiles\u2026';
    await new Promise((resolve) => {
      if (glMap && glMap.loaded() && glMap.isSourceLoaded('openmaptiles')) { resolve(); return; }
      if (!glMap) { resolve(); return; }
      const onIdle = () => { glMap.off('idle', onIdle); resolve(); };
      glMap.on('idle', onIdle);
      setTimeout(() => { glMap.off('idle', onIdle); resolve(); }, 8000);
    });

    const mapHidden = mapContainer.style.display === 'none';
    const widthKm = (mapHidden && lastWidthKm) ? lastWidthKm : mapWidthKm();
    lastWidthKm = widthKm;
    const widthMm = toMmDim(dimW), heightMm = toMmDim(dimH);

    restoreDetailBoost = await boostMapDetail(widthKm, heightMm / widthMm, msg => { loadMsg.textContent = msg; });

    loadMsg.textContent = 'Extracting features\u2026';
    await new Promise(r => setTimeout(r, 50));

    rawFeatureCache = extractRawFeatures(glMap, widthKm, msg => { loadMsg.textContent = msg; });

    if (restoreDetailBoost) { restoreDetailBoost(); restoreDetailBoost = null; }

    Object.keys(bandCache).forEach(k => delete bandCache[k]);
    featureCache = computeBands(rawFeatureCache, msg => { loadMsg.textContent = msg; }, heightMm / widthMm);
    bandCache[bandScale.toFixed(2)] = featureCache;
    Object.keys(layerCache).forEach(k => delete layerCache[k]);

    loadMsg.textContent = 'Building layers\u2026';
    await new Promise(r => setTimeout(r, 50));

    const refW = featureCache.refSize;
    const refH = featureCache.refSize * (heightMm / widthMm);
    const layers = buildLayers(featureCache, widthMm, heightMm, 0, 0, roadMode);
    lastResult = { widthMm, heightMm, panXmm: 0, panYmm: 0, layers };
    svgContainer.innerHTML = buildFullSvg(layers, refW, refH, 0, 0);
    dlBtn.disabled = false; var _dli = document.getElementById("dl-individual"); if (_dli) _dli.disabled = false;
    document.querySelectorAll('.post-generate-only').forEach(el => el.style.display = '');
    showSvg();
    document.getElementById('loading-overlay').style.display = 'none';
    setStatus('', 'ok');
  } catch (err) {
    document.getElementById('loading-overlay').style.display = 'none';
    setStatus(err.message || 'Error.', 'error');
  } finally {
    if (restoreDetailBoost) restoreDetailBoost();
    generateBtn.disabled = false;
  }
});

// Download
dlBtn.addEventListener('click', async () => {
  if (!lastResult || !featureCache) { setStatus('Generate a map first.', 'error'); return; }
  setStatus('Preparing SVG export...');
  try {
    const { widthMm, heightMm } = lastResult;
    const refW = featureCache.refSize;
    const refH = featureCache.refSize * (heightMm / widthMm);

    // Rebuild layers so the current roadThickMult (and any other layer-affecting
    // state) is applied to the exported filled road polygons.
    Object.keys(layerCache).forEach(k => delete layerCache[k]);
    const layers = buildLayers(featureCache, widthMm, heightMm, 0, 0, roadMode, true);
    lastResult = { widthMm, heightMm, panXmm: 0, panYmm: 0, layers };

    setStatus('Loading fonts for text conversion...');
    // Load fonts for text-to-path conversion
    const titleFont2 = await loadFont(titleFont);
    const interFont = await loadFont("Inter,Helvetica,sans-serif");

    const placeName = (document.getElementById('place-input').value || '').trim();
    const safeName = placeName ? placeName.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-').toLowerCase() : 'map';
    downloadSvg(
      buildLayeredExportSvg(layers, refW, refH, 0, 0, widthMm, heightMm, titleFont2, interFont),
      safeName + '-layered-map.svg'
    );
    setStatus('SVG downloaded.', 'ok');
  } catch (e) {
    setStatus('Export error: ' + e.message, 'error');
  }
});

// Individual layer download: downloads each layer as a separate SVG file
const dlIndividualBtn = document.getElementById('dl-individual');
if (dlIndividualBtn) {
  dlIndividualBtn.addEventListener('click', async () => {
    if (!_lastExportPanels || !_lastExportDims) {
      // Need to run the export first to build panels
      if (!lastResult || !featureCache) { setStatus('Generate a map first.', 'error'); return; }
      setStatus('Preparing layers...');
      try {
        const { widthMm, heightMm } = lastResult;
        const refW = featureCache.refSize;
        const refH = featureCache.refSize * (heightMm / widthMm);
        Object.keys(layerCache).forEach(k => delete layerCache[k]);
        const layers = buildLayers(featureCache, widthMm, heightMm, 0, 0, roadMode, true);
        lastResult = { widthMm, heightMm, panXmm: 0, panYmm: 0, layers };
        const titleFont2 = await loadFont(titleFont);
        const interFont = await loadFont("Inter,Helvetica,sans-serif");
        buildLayeredExportSvg(layers, refW, refH, 0, 0, widthMm, heightMm, titleFont2, interFont);
      } catch (e) {
        setStatus('Export error: ' + e.message, 'error');
        return;
      }
    }
    if (!_lastExportPanels || !_lastExportPanels.length) { setStatus('No layers to export.', 'error'); return; }

    const placeName = (document.getElementById('place-input').value || '').trim();
    const safeName = placeName ? placeName.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-').toLowerCase() : 'map';

    setStatus('Downloading ' + _lastExportPanels.length + ' layers...');
    for (let i = 0; i < _lastExportPanels.length; i++) {
      const svg = buildSingleLayerSvg(i);
      if (!svg) continue;
      const layerName = _lastExportPanels[i].label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
      // Stagger downloads so browser doesn't block them
      await new Promise(r => setTimeout(r, i * 500));
      downloadSvg(svg, safeName + '-' + layerName + '.svg');
    }
    setStatus(_lastExportPanels.length + ' layers downloaded.', 'ok');
  });
}

// Environment check
(function() {
  if (window.self !== window.top) {
    try { void window.top.location.href; } catch (_) {
      generateBtn.disabled = true; generateBtn.textContent = 'Requires a browser';
      mapContainer.innerHTML = '<div style="padding:40px;text-align:center;color:#888;">Download and open in Chrome/Firefox/Safari.</div>';
      return;
    }
  }
  if (location.protocol === 'file:') setStatus('Local file \u2014 try Firefox or: python3 -m http.server', 'error');
})();